/*
 * OrbitIntake — chat orchestrator (v2.7 / Phase 5 / brand-true reskin).
 *
 * Layout — two-pane shell on desktop:
 *
 *   ┌── topbar (logo · theme toggle) ─────────────────────────────┐
 *   │                                                              │
 *   │ ┌── chat (center, ~720) ────┐  ┌── dossier (right rail, ~  │
 *   │ │  hero (cold start only)   │  │   420) — default visible  │
 *   │ │  · turn · turn · …        │  │   counter · filter pills  │
 *   │ │  [education panel]         │  │   row · row · row …       │
 *   │ │  [approach choice]         │  │                            │
 *   │ │  composer (sticky)         │  │                            │
 *   │ └────────────────────────────┘  └───────────────────────────┘
 *   │                                                              │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Below 1000px the dossier collapses to a slide-in drawer (mobile only).
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Sun, Moon, ChevronRight, LayoutDashboard, CalendarClock, Sparkles, Mic } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useOrbitTheme } from './useOrbitTheme';

import { ChatTurn } from './dossier/ChatTurn';
import { IngestProgressCard } from './dossier/IngestProgressCard';
import { WebsiteStep } from './steps/WebsiteStep';
import { SectionCardFlow } from '../components/dossier/SectionCardFlow';
import { isFilledValue, subKey } from '../components/dossier/fieldKit';
import { AddFieldControl } from '../components/dossier/AddFieldControl';
import { CatchAllCard } from '../components/dossier/CatchAllCard';
import { StackToolsPanel } from '../components/dossier/StackToolsPanel';
import { GoalsPanel } from '../components/dossier/GoalsPanel';
import { DepartmentsPanel } from '../components/dossier/DepartmentsPanel';
import { fmtDate, toStringArray } from '../components/dossier/fieldKit';
import { Composer } from './dossier/Composer';
import { OrbitLiveVoice } from './dossier/OrbitLiveVoice';
import { BusinessWalk } from './dossier/BusinessWalk';
import { DossierDrawer } from './dossier/DossierDrawer';
import { EducationPanel } from './dossier/EducationPanel';
import { PaidHumanRecommendedPill } from './dossier/PaidHumanRecommendedPill';
import { RecommendationPill } from './dossier/RecommendationPill';
import { TurnPane } from './dossier/TurnPane';
import type { TurnPaneVariant } from './dossier/TurnPane';
import { OrbitThinking } from './dossier/OrbitThinking';
import { RouteChooser, type OrbitRoute } from './dossier/directives/RouteChooser';
import BuilderScopingPane from './BuilderScopingPane';
import { BatchConfirmCard } from './dossier/directives/BatchConfirmCard';
import { CompoundChipCard } from './dossier/directives/CompoundChipCard';
import { AnythingElseCard } from './dossier/directives/AnythingElseCard';
import { ProblemCanvas } from './dossier/directives/ProblemCanvas';
import { PrdDraftProgressCard } from './dossier/directives/PrdDraftProgressCard';
import { PrdInlineReviewCard } from './dossier/directives/PrdInlineReviewCard';
import { FieldSuggestionsCard } from './dossier/directives/FieldSuggestionsCard';
import { ScheduleCallModal } from './dossier/ScheduleCallModal';

import {
  fetchSchema,
  fetchState,
  getBusinessWalk,
  fetchHome,
  ingest,
  ingestFile,
  converse,
  converseStream,
  confirm as confirmApi,
  submit as submitApi,
  updateApproach,
  draftPrdNow,
  fetchEmailHints,
  shareField,
  enterCustomBuild,
  confirmDeepScoping,
  selectRoute,
  newProblemReset,
  resumeProblem,
  fetchFieldCandidates,
} from './lib/intakeApi';
import { requestAgentBridgeUrl } from '../services/api';
import { fetchConnections, type OnboardingConnectionRow } from '../services/onboardingApi';
import { scheduleSectionCall } from './lib/scopeApi';
import type { ConverseOptions, ShownQuestionPayload } from './lib/intakeApi';
import type {
  Approach,
  BatchConfirmPayload,
  BatchConfirmResponse,
  CompanyContext,
  ConfirmResponse,
  ConverseResponse,
  DirectivePayload,
  FieldDef,
  IngestResponse,
  NextQuestion,
  OrbitSession,
  PrdDocument,
  RoutePlan,
  SectionLetter,
  SuggestionResult,
} from './lib/dossier-types';

import './styles/orbit-tokens.css';
import './styles/orbit-intake.css';
import './styles/orbit-dossier.css';


const EMPTY_CTX: CompanyContext = {
  section_a: {}, section_b: {}, section_c: {}, section_d: {},
  section_e: {}, section_f: {}, section_g: {},
  field_states: {}, completion_pct: 0,
};


// Help-rejection regex — detects when the user pushes back on a
// suggestion (paid-human pill, marketplace card, PRD draft offer) OR
// explicitly affirms self-driven scoping. Matching this triggers an
// instant local pill dismissal in sendText so the user feels heard
// before the /converse round-trip (~5-10s).
//
// The backend's intake_agent.py rejection-handling rule fires in
// parallel and writes clarity_self_signal=deep + help_level=self_serve
// to the dossier, making the dismissal stick across reloads.
//
// Patterns intentionally permissive — false positives just hide a pill
// the user can re-summon via the suggestion if they change their mind.
const HELP_REJECTION_PATTERN = /\b(?:no\s+(?:solution\s+)?(?:partner|consultant|human|help)|don'?t\s+(?:need|want)\s+(?:a\s+|any\s+)?(?:solution\s+partner|consultant|human|help)|(?:i|we)\s+can\s+do\s+(?:it|this)\s+(?:myself|ourselves|alone)|(?:i|we)\s+have\s+clarity|the\s+problem\s+is\s+defined|don'?t\s+recommend|skip\s+(?:that|the)\s+(?:suggestion|recommendation|partner|card)|(?:i'?m|we'?re)\s+driving|we\s+know\s+what\s+we\s+want|no\s+thanks?|not\s+(?:interested|needed))\b/i;


// Compound multi-question batching kill-switch. The CompoundChipCard
// (N similar questions in one card with auto-advance) proved fragile —
// rows got stuck un-clickable, submit showed "Saved 0 of N", and the
// next question sometimes never appeared. For reliability we DISABLE
// it: when the backend returns a `compound_chips` payload, the frontend
// ignores it and falls back to the single `next_question` — Orbit asks
// one question at a time, the battle-tested flow. Flip to true only
// after the card is hardened + re-tested end to end.
const COMPOUND_CHIPS_ENABLED = false;

// L2 phase walk — banner label per phase token. Rendered above the
// question so the user always knows where they are in deep scoping.
// Phase 2 is the ProblemCanvas (its own surface), so it has no chat
// banner here; Phase 1 / Phase 3 questions carry these.
const PHASE_BANNER: Record<string, string> = {
  L2_PHASE_1: 'Phase 1 of 3 · Company shell',
  L2_PHASE_3: 'Phase 3 of 3 · Outcomes & success',
};


// `kind` lets the state-watcher avoid pushing duplicate recommendation /
// action turns when state re-fires. Persisted into localStorage too so a
// reload doesn't re-push them.
type OrbitTurnKind = 'normal' | 'recommend' | 'action';

interface OrbitActionData {
  label: string;
  route: string;
  detail?: string;
  /** When true, the click opens `route` in a new tab via window.open
   *  instead of react-router navigate. Used for validx workspace links
   *  bridged through requestAgentBridgeUrl. */
  external?: boolean;
}

type Turn =
  | {
      role: 'orbit';
      text: string;
      why?: string | null;
      chips?: NextQuestion['chips'];
      active?: boolean;
      field?: string | null;
      sectionTag?: string | null;
      kind?: OrbitTurnKind;
      variant?: 'normal' | 'recommend' | 'action';
      // Persisted action data — re-bound to onClick at render time.
      actionData?: OrbitActionData | null;
      // v0.2 — agent's `render` tool emits a directive_type on this
      // turn. The ChatTurn renderer mounts the matching primitive
      // (ConfirmCard / OfferResearchCard / RouteCard / ScalePicker).
      directiveType?: NextQuestion['directive_type'] | null;
      directivePayload?: NextQuestion['directive_payload'] | null;
      // L2 phase walk — when set ('L2_PHASE_1' | 'L2_PHASE_3'), the turn
      // renders a small phase banner ("Phase 1 of 3 · Company shell") so
      // the user knows where they are in the deep-scoping walk.
      phase?: string | null;
      // Conversational answer Orbit gave THIS turn (e.g. answering "do you
      // know our website?"). Rendered as a block ABOVE the question so the
      // user always sees Orbit listened, with the question still below it.
      reply?: string | null;
      // Proactive coaching — generated option blocks for a free-type field,
      // rendered INLINE under THIS question (so the question stays visible)
      // instead of as a separate pane turn that would hide it.
      fieldSuggestions?: import('./lib/dossier-types').FieldSuggestionsPayload | null;
    }
  | { role: 'user'; text: string; attachment?: 'url' | 'doc' | 'voice' | null }
  | { role: 'system'; text: string; kind?: 'info' | 'success' | 'warn' }
  // Slice 4 — Sharp-fill BatchConfirm. Renders an inline card showing
  // all just-extracted ai_guessed fields with per-row keep/edit/skip
  // + master "Looks right" CTA. Replaces N Inquirer turns with one
  // batch review after any ingest (URL / doc / paste / voice).
  | { role: 'batch_confirm'; payload: import('./lib/dossier-types').BatchConfirmPayload; submitted?: boolean }
  // Slice 4 — Sharp-fill CompoundChips. Renders a multi-row chip card
  // when the primary next-question has 1-3 same-section chip siblings
  // (e.g. G's hands-on / urgency / buyer / authority answered in one
  // card instead of 4 turns).
  | { role: 'compound_chips'; payload: import('./lib/dossier-types').CompoundChipsPayload; submitted?: boolean }
  // Slice 4 — Section-boundary "anything else?" terminal turn. Fires
  // once per high-yield L1 section (a / b / e). Prose routes through
  // paste-ingest; any new deltas surface as a BatchConfirmCard.
  | { role: 'anything_else'; payload: import('./lib/dossier-types').AnythingElsePayload; submitted?: boolean }
  // Slice 7 — Phase 2 ProblemCanvas. Sticky pinned card showing the
  // one problem we're scoping in depth (seeded from L1.E). User can
  // edit the statement, add workflows, or skip ahead to draft PRD.
  | { role: 'problem_canvas'; payload: import('./lib/dossier-types').ProblemCanvasPayload; closed?: boolean }
  // Slice 12 — PRD draft live progress. Polls /home for the latest
  // routing_decision.prd_draft_status and transitions to "Open
  // workspace" once the validx project lands.
  | { role: 'prd_progress'; payload: import('./lib/dossier-types').PrdDraftProgressPayload }
  // Slice 12 v2 — inline PRD review after the draft lands. Shows the
  // 9 sections expandable, with approve / talk-to-partner / add-more
  // CTAs. Approve flips engagement to BUILD_QUEUED and opens the
  // validx workspace via the HMAC bridge.
  | { role: 'prd_review'; payload: import('./lib/dossier-types').PrdInlineReviewPayload; approved?: boolean }
  // Coaching mode — when the user asks for help defining the active
  // longtext field, the agent calls propose_field_suggestions and
  // this turn renders 2-3 tap-to-commit candidate chips. Tapping a
  // chip fires sendText(candidate.value).
  | { role: 'field_suggestions'; payload: import('./lib/dossier-types').FieldSuggestionsPayload; picked?: boolean }
  // Professional staged status while Orbit reads a shared artifact — a URL
  // (website / any link) OR an uploaded document. Shows "Reading {label} →
  // understanding what's relevant" with a pulse; removed when the read
  // returns + replaced by the result (the understanding summary). `kind`
  // tunes the copy (a site vs a file); `host` is the display label.
  | { role: 'url_reading'; host: string; kind?: 'url' | 'doc' }
  // Orbit redesign 6a — the landing chooser. Three big branded cards (Solve a
  // problem / Map the business / Explore). Pick routes into the path.
  | { role: 'route_chooser' }
  // A1 — the very first thing on a cold start, ahead of the chooser: give us
  // something to read (a site, a document, or a description). Whatever lands
  // here fills the dossier, so every card after it opens already filled.
  | {
      role: 'website_intake';
      /** Why the last attempt did not take. Present only on a retry. */
      error?: string;
      /** What they typed last time, so a typo is corrected, not retyped. */
      lastUrl?: string;
    };

function chipsWithSkipMaybe(q: NextQuestion | null): NextQuestion['chips'] {
  if (!q) return [];
  const base = q.chips ?? [];
  if ((q.inquired_count ?? 0) >= 2) {
    return [...base, { value: '__skip_this', label: 'Skip this one' }];
  }
  return base;
}

function questionFromTurn(turn: Turn | undefined, fields: FieldDef[]): NextQuestion | null {
  if (!turn || turn.role !== 'orbit' || !turn.field) return null;
  const fieldDef = fields.find((f) => f.key === turn.field);
  if (!fieldDef) return null;
  return {
    field: fieldDef.key,
    label: fieldDef.label,
    prompt: turn.text,
    why: turn.why ?? fieldDef.why ?? null,
    kind: fieldDef.kind,
    chips: fieldDef.options ?? [],
    placeholder: fieldDef.placeholder ?? null,
    is_confirming_guess: false,
    current_guess: null,
  };
}

function recoverActiveQuestion(turns: Turn[], fields: FieldDef[]): NextQuestion | null {
  if (fields.length === 0 || turns.length === 0) return null;
  for (let i = turns.length - 1; i >= 0; i -= 1) {
    const recovered = questionFromTurn(turns[i], fields);
    if (recovered) return recovered;
  }
  return null;
}

function shownQuestionPayload(q: NextQuestion | null): ShownQuestionPayload | null {
  if (!q) return null;
  return {
    field: q.field,
    prompt: q.prompt,
    chip_values: (q.chips ?? []).map((chip) => chip.value),
  };
}

/**
 * Derive shown_question from the most recent rendered ORBIT turn — not
 * just the registry-backed `activeQuestion`. The cold-start hero is a
 * real orbit turn with chips but no registry field; the agent still
 * needs its text + chip values to read the user's reply in context.
 *
 * Falls back to `activeQuestion` when no orbit turn is in the history
 * (shouldn't happen post-cold-start, but defensive).
 */
function shownQuestionFromHistory(
  turns: Turn[],
  activeQuestion: NextQuestion | null,
): ShownQuestionPayload | null {
  for (let i = turns.length - 1; i >= 0; i -= 1) {
    const t = turns[i];
    if (t.role !== 'orbit') continue;
    const orbitTurn = t as Extract<Turn, { role: 'orbit' }>;
    return {
      field: orbitTurn.field ?? activeQuestion?.field ?? null,
      prompt: orbitTurn.text || null,
      chip_values: (orbitTurn.chips ?? []).map((c) => c.value),
    };
  }
  return shownQuestionPayload(activeQuestion);
}

function routeDriftAnnouncement(plan: RoutePlan): string {
  const reason = plan.route_drift_reason?.trim();
  return reason ? `Route changed. ${reason}` : 'Route changed. Your latest answers point to a different path.';
}


// The fresh problem opener — shown whenever "Scope a problem" starts a new
// problem. Kept in one place so handleRoutePick and the fresh-start path
// stay identical.
const PROBLEM_OPENER_TEXT =
  "Walk me through the problem — what's broken, who it affects, how you handle it today, and whether you've tried fixing it before.";
const PROBLEM_OPENER_WHY =
  "One answer can cover a lot. I'll pull out what I need and only ask what's still unclear.";

export interface OrbitIntakeProps {
  /** 'page' = full-page /orbit/intake (default). 'panel' = mounted inside
   *  the dashboard side-drawer — same Orbit, same logic, same cards;
   *  only the outer chrome differs (the drawer supplies its own header,
   *  so we drop the topbar and let the pane fill the drawer width). One
   *  Orbit, two mounts. */
  variant?: 'page' | 'panel';
  /** Host refresh hook — called after each advancing turn so a host page
   *  (My Business) can re-pull its scoping-status card. */
  onAfterTurn?: () => void;
  /** Conversation context. The turns/active-index/ui-state localStorage keys
   *  are suffixed with this so each context keeps its OWN thread instead of
   *  one shared thread that leaks across surfaces:
   *    'company'        → My Business (company A–D mapping)
   *    'problem'        → "Scope a problem" (a new problem intake)
   *    `problem:<id>`   → a specific in-progress problem
   *  Default '' = the legacy shared thread. */
  context?: string;
  /** When true, ignore any stored thread for this context and start on the
   *  fresh problem opener (first problem question), and assert the problem
   *  route. This is what makes "Scope a problem" ALWAYS start new. */
  freshProblem?: boolean;
  /** Side-panel drawer is expanded to full-screen. When true the panel shows
   *  full card detail (like the intake page); when false (default side panel)
   *  directive cards render compact (heading + buttons). */
  expanded?: boolean;
  /** Optional one-shot prompt submitted after the panel opens. Used by
   *  connected-app quick-win chips so they enter the normal problem-scoping
   *  pipeline instead of inventing a separate workflow path. */
  seedPrompt?: string | null;
  /** Hidden route metadata for seeded tool/app launches. This keeps internal
   *  routing hints out of the visible "You said" message. */
  seedMeta?: {
    workRoute?: string | null;
    source?: string | null;
    provider?: string | null;
  } | null;
}

// Voice is the default way in wherever the intake renders, so the mic probe is
// module-level: it never changes for the life of the page.
const MIC_AVAILABLE = (() => {
  try { return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia; }
  catch { return false; }
})();

export function OrbitIntake({ variant = 'page', onAfterTurn, context = '', freshProblem = false, expanded = false, seedPrompt = null, seedMeta = null }: OrbitIntakeProps = {}) {
  const isPanel = variant === 'panel';
  // Problem scoping runs the builder's Orbit plugin over MCP (its methodology,
  // its tools, our UI). Flag-gated so an unreachable builder falls back to the
  // built-in flow instead of leaving the user on a dead screen.
  const pluginScopingEnabled = process.env.REACT_APP_PLUGIN_SCOPING !== '0';
  const [pluginScoping, setPluginScoping] = useState(false);
  // Compact directive cards (PRD / shelf) only in the COLLAPSED side panel.
  // The intake page and the expanded full-screen drawer show full detail.
  const compactCards = isPanel && !expanded;
  const _ctxSuffix = context ? `:${context}` : '';

  // Multi-problem (P2b): which problem record this chat is bound to. A
  // `problem:<id>` context resumes that problem; a fresh "Scope a problem"
  // (context 'problem' + freshProblem) starts a new one and adopts the id the
  // server mints on the describe turn.
  const _ctxProblemId = context.startsWith('problem:') ? context.slice('problem:'.length) : null;
  const [activeProblemId, setActiveProblemId] = useState<string | null>(_ctxProblemId);
  // Mirror of activeProblemId for synchronous reads inside handlers (avoids a
  // stale closure and lets _adoptProblem detect a genuinely-new id without
  // putting side effects in the setState updater).
  const activeProblemIdRef = useRef<string | null>(_ctxProblemId);
  useEffect(() => { activeProblemIdRef.current = activeProblemId; }, [activeProblemId]);
  // The landing route this chat is on, held in a ref so every turn can declare
  // it to the backend (which re-asserts it) with no render-closure staleness.
  // Synced from ctx below, and set eagerly the moment a route is picked — this
  // is what stops a stale per-client 'problem' route from hijacking the
  // company (map-the-business) walk.
  const routeRef = useRef<OrbitRoute | null>(null);
  const _converseOpts = (): ConverseOptions => ({
    problemId: activeProblemId,
    newProblem: !!freshProblem && !activeProblemId,
    workRoute: seedMeta?.workRoute ?? null,
    source: seedMeta?.source ?? null,
    provider: seedMeta?.provider ?? null,
    route: routeRef.current,
  });
  const _adoptProblem = (r: { problem_id?: string | null } | null | undefined) => {
    if (!r || !r.problem_id) return;
    const pid = r.problem_id;
    // When the describe turn MINTS a new problem (an id we didn't have before),
    // announce it so any surface can react in real time (the Problems list
    // refreshes even when mounted behind the global drawer). Compare against the
    // CURRENT value via a ref read — never dispatch inside the setState updater,
    // or ClientProblemsPage.load() runs during OrbitIntake's render ("cannot
    // update a component while rendering another") and can drop this turn's
    // next-question. Defer the event out of the render/commit phase.
    const isNew = pid !== activeProblemIdRef.current;
    setActiveProblemId(pid);
    if (isNew) {
      queueMicrotask(() => {
        try {
          window.dispatchEvent(new CustomEvent('orbit:problem-created', {
            detail: { problem_id: pid },
          }));
        } catch { /* no-op */ }
      });
    }
  };
  const { theme, toggle: toggleTheme } = useOrbitTheme();
  const navigate = useNavigate();

  // ONE exit for every door into Map the Business — speaking, typing, or
  // tapping through the walk. A session that produced a problem (or a PRD
  // drafted for one) ends ON that problem, because that is the page showing
  // what was just made; a session that only mapped the company ends on the
  // business. Nothing ever ends on a card with nowhere to go.
  // Sending someone somewhere means closing over the drawer when we ARE the
  // drawer. Navigating alone changed the address under an open panel, so the
  // button read as broken while it had in fact worked.
  const goAndClose = useCallback((to: string) => {
    navigate(to);
    if (isPanel) window.dispatchEvent(new Event('orbit:close'));
  }, [navigate, isPanel]);

  const exitOrbit = useCallback((opts?: { problemId?: string | null; home?: string }) => {
    const go = goAndClose;
    const pid = opts?.problemId ?? activeProblemIdRef.current;
    if (pid) { go(`/problems/${pid}`); return; }
    // A caller that KNOWS where it belongs says so. routeRef alone was not
    // enough: it is only set once a route has been picked or persisted, so the
    // company walk reached through the drawer finished with it still null and
    // dropped the person on the dashboard instead of their business.
    if (opts?.home) { go(opts.home); return; }
    if (routeRef.current === 'company') { go('/my-business'); return; }
    if (!isPanel) navigate('/dashboard');
  }, [navigate, isPanel, goAndClose]);

  // REACTIVE client_id. Previously a one-shot useMemo([]) — if OrbitIntake
  // mounted before auth wrote `client_id` (or during the silent Google
  // re-auth that clears+resets it on reload), clientId stayed null forever
  // and the turn-persistence effects below short-circuited (`if (!clientId)
  // return`), so NOTHING was saved → every reload lost the conversation.
  // Now we re-read on mount, on `storage` events, and on window focus, so
  // the moment auth lands, persistence + hydration key off the live id.
  const [clientId, setClientId] = useState<string | null>(() => {
    try { return window.localStorage.getItem('client_id'); } catch { return null; }
  });

  // Voice first, text second. The composer stays hidden while Orbit talks and
  // is revealed by "type instead"; with no mic there is nothing to hide it for,
  // so typing is shown up front.
  const [showText, setShowText] = useState<boolean>(!MIC_AVAILABLE);

  useEffect(() => {
    const read = () => {
      try {
        const cid = window.localStorage.getItem('client_id');
        setClientId(prev => (prev === cid ? prev : cid));
      } catch { /* ignore */ }
    };
    read();
    window.addEventListener('storage', read);
    window.addEventListener('focus', read);
    return () => {
      window.removeEventListener('storage', read);
      window.removeEventListener('focus', read);
    };
  }, []);

  // Fresh "Scope a problem" start → assert the problem route so the backend
  // inquirer scopes to the problem section (E), never company. The opener
  // is already seeded in the turns initializer above.
  useEffect(() => {
    if (freshProblem && clientId) {
      selectRoute('problem', clientId).catch(() => { /* best effort */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freshProblem, clientId]);
  // Corporate email domain — if user signed up with @yourcompany.com (not a
  // personal-email provider), the domain is almost certainly their site.
  const corporateDomain = useMemo(() => {
    try {
      const email = (window.localStorage.getItem('user_email') || '').trim().toLowerCase();
      if (!email || !email.includes('@')) return null;
      const domain = email.split('@')[1];
      if (!domain) return null;
      const personal = new Set([
        'gmail.com', 'googlemail.com',
        'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
        'yahoo.com', 'yahoo.co.uk', 'yahoo.in', 'ymail.com',
        'icloud.com', 'me.com', 'mac.com',
        'aol.com', 'aim.com',
        'protonmail.com', 'proton.me', 'pm.me',
        'mail.com', 'gmx.com', 'gmx.net',
        'yandex.com', 'yandex.ru',
        'fastmail.com', 'inbox.com', 'zoho.com',
        'hey.com', 'tutanota.com',
      ]);
      return personal.has(domain) ? null : domain;
    } catch {
      return null;
    }
  }, []);

  // ── Unified UI-state localStorage blob ────────────────────────────
  // Persists the smaller bits of UI state that ARE'NT covered by:
  //   - turns          (orbit:turns:v2:<cid>)
  //   - activeTurnIdx  (orbit:active-turn-idx:v1:<cid>)
  //   - ctx + counts   (hydrate from /state on every mount)
  //   - routePlan      (hydrate from /state on every mount)
  //
  // Without this blob, reload would lose:
  //   - `question` (next-question shape — but we also recover from the
  //     latest orbit turn + registry, so this is a fast-path only)
  //   - `suggestion` (drives PaidHumanRecommendedPill visibility — no
  //     server-side equivalent; if not persisted, the pill won't
  //     re-show until the next /converse turn)
  //   - `paidHumanPillOpened` (once dismissed, must stay dismissed
  //     across reloads — otherwise it pops back up annoyingly)
  //   - `eduDismissed` (once acknowledged, must stay acknowledged)
  //   - `prdDrafting` (mid-flight PRD draft state — without it, the
  //     progress card disappears on reload while the backend is still
  //     drafting)
  //
  // Stored as a single JSON blob under `orbit:ui-state:v1:<cid>` so a
  // single useEffect can write all of it on any change.
  const UI_STATE_KEY = 'orbit:ui-state:v1' + _ctxSuffix;
  const _loadInitialUiState = (): Partial<{
    question: NextQuestion | null;
    suggestion: SuggestionResult | null;
    paidHumanPillOpened: boolean;
    eduDismissed: boolean;
    prdDrafting: boolean;
  }> => {
    // A fresh "Scope a problem" open must start truly clean, AND a per-problem
    // resume must reflect that problem's LIVE state — not a persisted active
    // question. Without this guard the stale active QUESTION / suggestion /
    // prdDrafting rehydrated from the blob, so the composer showed an
    // already-answered question (e.g. acceptance criteria) on every open. In
    // both cases the live question is derived after checkout, so start clean.
    if (freshProblem || _ctxProblemId) return {};
    try {
      const cid = window.localStorage.getItem('client_id');
      if (!cid) return {};
      const raw = window.localStorage.getItem(`${UI_STATE_KEY}:${cid}`);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch {
      return {};
    }
  };

  const [fields, setFields] = useState<FieldDef[]>([]);
  const [sections, setSections] = useState<Partial<Record<SectionLetter, string>>>({
    a: 'Company', b: 'Stack', c: 'Org', d: 'Goals',
    e: 'Problem', f: 'Budget', g: 'Engagement',
  });
  const [ctx, setCtx] = useState<CompanyContext>(EMPTY_CTX);

  // Is this mount on the COMPANY route (Map the Business)?
  //
  // Two things hang off this. (1) The composer's commercial approach pills are
  // a PROBLEM decision and must never render here. (2) More importantly, the
  // company route is a WALK, not a conversation — so no LLM turn may run on
  // it. That second one is load-bearing: `extract_field` at confidence ≥0.8
  // commits as user_confirmed, which outranks the state guard, so a stray
  // converse('') here would re-extract stale facts from the thread and
  // overwrite what the person had just corrected in the walk (the bug: "I
  // entered a new website and it reverted to the pre-filled one").
  //
  // The My Business drawer mounts with context="company" but never runs
  // through the route chooser, so `_selected_route` is unset (or stale from a
  // prior problem) — gate on the explicit mount context FIRST, then fall back
  // to the route field-state for the full-page company route.
  const isCompanyRoute = useMemo(() => {
    if (context === 'company' || context.startsWith('company')) return true;
    const fs = ctx.field_states ?? {};
    const routeVal = ((fs['_selected_route'] as { value?: string } | undefined)?.value
      ?? (fs['_path'] as { value?: string } | undefined)?.value);
    return routeVal === 'company';
  }, [context, ctx.field_states]);
  // Mirror the persisted/optimistic route into the ref that every turn reads.
  useEffect(() => {
    const picked = (ctx.field_states as Record<string, { value?: string } | undefined> | undefined)
      ?.['_selected_route']?.value;
    if (picked === 'problem' || picked === 'company' || picked === 'explore') routeRef.current = picked;
  }, [ctx.field_states]);
  const [question, setQuestion] = useState<NextQuestion | null>(
    () => _loadInitialUiState().question ?? null,
  );
  const [suggestion, setSuggestion] = useState<SuggestionResult | null>(
    () => _loadInitialUiState().suggestion ?? null,
  );
  const [counts, setCounts] = useState({ captured: 0, total: 0 });
  const [locked, setLocked] = useState(false);
  const [routePlan, setRoutePlan] = useState<RoutePlan | null>(null);
  const [orbitSession, setOrbitSession] = useState<OrbitSession | null>(null);

  // Hydrate turns from localStorage on mount so the conversation survives
  // reloads. Per-client key so different sessions don't bleed into each other.
  // We strip legacy 'recommend' and 'action' kinds — those used to be chat
  // surfaces but now live in the welded RecommendationPill instead.
  // Storage key version. Bump when the opener copy changes so stale
  // turns (e.g. the old "Yes, scan brndsh.io" / "That's not us" chips
  // that the email-prefill BatchConfirmCard made redundant) don't
  // rehydrate and confuse the first impression.
  const TURNS_STORAGE_KEY = 'orbit:turns:v2' + _ctxSuffix;
  const [turns, setTurns] = useState<Turn[]>(() => {
    // "Scope a problem" → always begin on the fresh problem opener,
    // ignoring any stored thread for this context.
    if (freshProblem) {
      return [{ role: 'orbit', text: PROBLEM_OPENER_TEXT, why: PROBLEM_OPENER_WHY, active: true }];
    }
    try {
      const cid = window.localStorage.getItem('client_id');
      if (!cid) return [];
      const raw = window.localStorage.getItem(`${TURNS_STORAGE_KEY}:${cid}`);
      if (!raw) {
        // Drop legacy unversioned cache so the new opener seeds cleanly.
        try { window.localStorage.removeItem(`orbit:turns:${cid}`); } catch {}
        return [];
      }
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return (parsed as Turn[]).filter(t => {
        if (t.role !== 'orbit') return true;
        const k = (t as { kind?: OrbitTurnKind }).kind;
        return k !== 'recommend' && k !== 'action';
      });
    } catch {
      return [];
    }
  });
  const [pending, setPending] = useState(false);
  // Live "what Orbit understood" line streamed mid-turn (SSE) — shown in the
  // thinking indicator so the user sees progress in ~2s instead of a 40s
  // spinner. Cleared when the turn settles.
  const [streamAck, setStreamAck] = useState<string | null>(null);
  // Live reply text streamed mid-turn (SSE) — types into the thinking bubble
  // word-group by word-group, then gets replaced by the committed orbit turn
  // on `done`. Cleared when the turn settles.
  const [streamReply, setStreamReply] = useState('');
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  // Topbar "Pick up later" → menu (dashboard / schedule a call) + the
  // schedule-call modal (Orbit phones the user to resume the intake).
  const [laterMenuOpen, setLaterMenuOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [picking, setPicking] = useState<Approach | null>(null);
  const [eduDismissed, setEduDismissed] = useState<boolean>(
    () => Boolean(_loadInitialUiState().eduDismissed),
  );

  // v0.5 — directive-driven flow state.
  // prdDrafting: true while the validx background task creates the
  //   project + drafts the PRD. Frontend shows a system turn + polls
  //   /home for the workspace URL when it lands. Persisted to survive
  //   reload mid-flight so the progress card doesn't vanish.
  // marketplacePicking: which CTA in the marketplace_matches card is
  //   mid-write ('marketplace' | 'custom' | null). Transient (cleared
  //   on the next response), so not persisted.
  const [prdDrafting, setPrdDrafting] = useState<boolean>(
    () => Boolean(_loadInitialUiState().prdDrafting),
  );
  const [marketplacePicking, setMarketplacePicking] = useState<'marketplace' | 'custom' | null>(null);
  // Soft Solution-partner recommendation: tracks whether the user has
  // already clicked the pill or already saw the hard auto-injected card
  // this session. Once true, the pill stays dismissed for the rest of
  // the session — we don't nag. Persisted so reload doesn't re-pop it.
  const [paidHumanPillOpened, setPaidHumanPillOpened] = useState<boolean>(
    () => Boolean(_loadInitialUiState().paidHumanPillOpened),
  );
  // Booked already? Pulled from chat-turn history at render time so it
  // survives reloads (turns are cached in localStorage per-client).
  const paidHumanAlreadyBooked = useMemo(
    () => turns.some(
      (t) => t.role === 'orbit' && typeof t.text === 'string' && t.text.startsWith('Session booked'),
    ),
    [turns],
  );
  // Becomes true after BOTH /schema and /state resolve. The opener waits
  // on this so we never seed a cold-start greeting while the dossier
  // payload is still inflight.
  const [bootstrapReady, setBootstrapReady] = useState(false);

  const pushTurn = useCallback((turn: Turn) => {
    // L2 Phase-2 hop — the phase machine surfaces problem scoping as an
    // `enter_problem_scoping` directive on an orbit turn. Catch it HERE
    // (the single choke point every push path flows through) and convert
    // it into a pinned problem_canvas turn, so the transition works no
    // matter which endpoint (converse / confirm / phase entry) produced it.
    if (turn.role === 'orbit' && turn.directiveType === 'enter_problem_scoping') {
      type _PC = import('./lib/dossier-types').ProblemCanvasPayload;
      const p = (turn.directivePayload || {}) as _PC & Record<string, unknown>;
      setTurns(t => [...t, {
        role: 'problem_canvas',
        payload: {
          engagement_id: (p.engagement_id as string) || '',
          problem: (p.problem as _PC['problem']) || (null as unknown as _PC['problem']),
          workflows: [],
        },
      }]);
      return;
    }
    setTurns(t => [...t, turn]);
  }, []);

  // ── Pane mode — single-active-turn nav ──────────────────────────
  // `activeTurnIdx` is the turn currently filling the pane. When a
  // new turn is pushed we snap forward to it; prev/next buttons let
  // the user swipe back to review past turns. -1 means "no turn yet"
  // (cold start before the opener lands).
  //
  // Hydration: on mount, initialize from localStorage (a) so reload
  // restores the user's prev/next position exactly, (b) so the empty
  // state doesn't flash when turns are already cached. The value is
  // clamped to the hydrated turns length below in case the user wiped
  // turn cache but not idx cache.
  const ACTIVE_IDX_KEY = 'orbit:active-turn-idx:v1' + _ctxSuffix;
  const [activeTurnIdx, setActiveTurnIdx] = useState<number>(() => {
    // Fresh problem start → pane sits on the single opener turn.
    if (freshProblem) return 0;
    try {
      const cid = window.localStorage.getItem('client_id');
      if (!cid) return turns.length > 0 ? turns.length - 1 : -1;
      const raw = window.localStorage.getItem(`${ACTIVE_IDX_KEY}:${cid}`);
      if (raw != null) {
        const n = parseInt(raw, 10);
        if (Number.isFinite(n)) {
          // Clamp to the hydrated turns length so stale idx never
          // exceeds the array.
          if (turns.length === 0) return -1;
          return Math.min(Math.max(n, 0), turns.length - 1);
        }
      }
      return turns.length > 0 ? turns.length - 1 : -1;
    } catch {
      return turns.length > 0 ? turns.length - 1 : -1;
    }
  });

  // Persist the active idx so reload restores the user's position
  // exactly (incl. swiped-back-for-review state). Clamps separately
  // when turns shrink unexpectedly.
  useEffect(() => {
    if (!clientId) return;
    try {
      window.localStorage.setItem(
        `${ACTIVE_IDX_KEY}:${clientId}`,
        String(activeTurnIdx),
      );
    } catch {
      // storage full / private mode — drop silently
    }
  }, [activeTurnIdx, clientId]);

  // Cold-start welcome HOLD. On a fresh landing we pin the pane to the
  // welcome opener so the user reads Orbit's purpose + invitation first.
  // Without this, an async push (the email-prefill BatchConfirmCard
  // from /email-hints) would snap the pane forward and bury the welcome
  // — exactly the "where did my welcome go?" bug. The hold clears the
  // moment the user acts (types / pastes / picks), after which normal
  // auto-advance resumes.
  const holdWelcomeRef = useRef(false);
  // The opener the Room's confirm card stands in front of. Pushed once that
  // card is answered, so the chooser never sits behind an unread card.
  const roomOpenerRef = useRef<Turn | null>(null);
  // Set when a transition REPLACES turns instead of appending, so the pane has
  // to move even though the array did not grow (the auto-advance below only
  // fires on growth). Consumed once by that effect.
  const forcePaneToLastRef = useRef(false);
  const releaseWelcomeHold = useCallback(() => {
    holdWelcomeRef.current = false;
  }, []);

  // Whenever turns grow, advance to the newest NAVIGABLE turn (orbit /
  // card — never a bare system note, which would show an error/status
  // line in the focal pane). We use a ref-like pattern: only snap when
  // the array actually grew. Initialized to the hydrated length so the
  // first effect-run doesn't phantom-advance over the persisted idx.
  const prevTurnsLenRef = useRef(turns.length);
  // useLayoutEffect (not useEffect) so the pane snaps to the new turn
  // BEFORE the browser paints — otherwise there's a one-frame flash of
  // the previous turn (the "blink" when Orbit's next turn appears).
  useLayoutEffect(() => {
    if (forcePaneToLastRef.current) {
      forcePaneToLastRef.current = false;
      let forced = turns.length - 1;
      for (let i = turns.length - 1; i >= 0; i -= 1) {
        if (turns[i].role !== 'system') { forced = i; break; }
      }
      setActiveTurnIdx(forced);
      prevTurnsLenRef.current = turns.length;
      return;
    }
    if (turns.length > prevTurnsLenRef.current) {
      // Welcome hold — keep the pane on the welcome opener until the
      // user acts. Track the length so the hold-release resumes cleanly.
      if (holdWelcomeRef.current) {
        prevTurnsLenRef.current = turns.length;
        return;
      }
      // Advance to the last NON-system turn so the pane never lands on
      // a status/error note. Falls back to the true last turn.
      let target = turns.length - 1;
      for (let i = turns.length - 1; i >= 0; i -= 1) {
        if (turns[i].role !== 'system') { target = i; break; }
      }
      setActiveTurnIdx(target);
    }
    prevTurnsLenRef.current = turns.length;
  }, [turns.length, turns]);

  // Nav skips user / system turns — a question + its answer should
  // ride together on one pane (rendered as Q on top + your-answer
  // block below). Otherwise swiping back lands on the bare user
  // bubble first, then the question — the user has to flip twice
  // to see the exchange they're reviewing.
  const isNavigableTurn = useCallback((t: Turn | undefined): boolean => {
    if (!t) return false;
    return t.role !== 'user' && t.role !== 'system';
  }, []);
  const goPrev = useCallback(() => {
    setActiveTurnIdx(i => {
      for (let j = i - 1; j >= 0; j -= 1) {
        if (isNavigableTurn(turns[j])) return j;
      }
      return i;
    });
  }, [turns, isNavigableTurn]);
  const goNext = useCallback(() => {
    setActiveTurnIdx(i => {
      for (let j = i + 1; j < turns.length; j += 1) {
        if (isNavigableTurn(turns[j])) return j;
      }
      return i;
    });
  }, [turns, isNavigableTurn]);
  const goLatest = useCallback(() => {
    for (let j = turns.length - 1; j >= 0; j -= 1) {
      if (isNavigableTurn(turns[j])) { setActiveTurnIdx(j); return; }
    }
    setActiveTurnIdx(Math.max(0, turns.length - 1));
  }, [turns, isNavigableTurn]);

  // The user's answer (if any) that pairs with the active orbit turn.
  // Used to render a "Your answer" block under the question on past
  // panes. Returns null when no user reply followed yet (latest turn).
  type UserTurn = Extract<Turn, { role: 'user' }>;
  const pairedUserAnswer = useMemo<UserTurn | null>(() => {
    if (activeTurnIdx < 0) return null;
    const head = turns[activeTurnIdx];
    if (!head || (head.role !== 'orbit')) return null;
    const next = turns[activeTurnIdx + 1];
    if (next && next.role === 'user') return next;
    return null;
  }, [turns, activeTurnIdx]);

  // Breadcrumb: the most recent user turn BEFORE the active orbit turn,
  // walking back through any intermediate orbit-reply or system turns.
  // Critical UX patch — when Orbit re-asks the same field (e.g. voice
  // guard scrubbed the coaching reply, agent fell back), the new pane
  // looks identical to the previous one. Without this breadcrumb the
  // user feels like "nothing happened" because their input is hidden
  // off-screen. Walk capped to 5 turns back so we don't surface
  // unrelated history. Suppressed when pairedUserAnswer is set (past
  // pane already shows the answer below the question).
  const precedingUserContext = useMemo<UserTurn | null>(() => {
    if (activeTurnIdx <= 0) return null;
    const head = turns[activeTurnIdx];
    if (!head || head.role !== 'orbit') return null;
    if (pairedUserAnswer) return null;
    const stopAt = Math.max(0, activeTurnIdx - 5);
    for (let j = activeTurnIdx - 1; j >= stopAt; j -= 1) {
      const t = turns[j];
      if (!t) continue;
      if (t.role === 'user') return t;
      // System notes don't end the search; another orbit/directive
      // does (that was answered by a different user turn).
      if (t.role === 'orbit') continue;
      if (t.role === 'system') continue;
      // directive cards — break (separate exchange).
      break;
    }
    return null;
  }, [turns, activeTurnIdx, pairedUserAnswer]);

  // Counter math — "03 / 12" should count navigable turns only, not
  // raw array indices (which include user/system turns).
  const navTurnIndices = useMemo(() => {
    const out: number[] = [];
    turns.forEach((t, i) => { if (isNavigableTurn(t)) out.push(i); });
    return out;
  }, [turns, isNavigableTurn]);
  const navPos = navTurnIndices.indexOf(activeTurnIdx);
  const navTotal = navTurnIndices.length;

  // Keyboard nav — ← / → between turns, Esc snaps to latest.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Ignore when focus is inside an input / textarea / contenteditable.
      const tgt = e.target as HTMLElement | null;
      if (tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA' || tgt.isContentEditable)) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
      else if (e.key === 'Escape') { e.preventDefault(); goLatest(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goPrev, goNext, goLatest]);

  // Touch swipe nav — horizontal swipe on the stage fires prev/next.
  // Threshold: 60px horizontal AND less than 2x vertical (i.e. mostly
  // horizontal — distinguishes swipe from a tap-scroll). Ignored when
  // the touch starts inside an input / textarea so typing isn't hijacked.
  const swipeStateRef = useRef<{ x: number; y: number; t: number } | null>(null);
  const handleStageTouchStart = useCallback((e: React.TouchEvent) => {
    const tgt = e.target as HTMLElement | null;
    if (tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA' || tgt.isContentEditable)) {
      swipeStateRef.current = null;
      return;
    }
    if (e.touches.length !== 1) {
      swipeStateRef.current = null;
      return;
    }
    const t = e.touches[0];
    swipeStateRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  }, []);
  const handleStageTouchEnd = useCallback((e: React.TouchEvent) => {
    const start = swipeStateRef.current;
    swipeStateRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const dt = Date.now() - start.t;
    // Must be a quick, mostly-horizontal flick. Threshold 60px so a
    // small mis-tap doesn't paginate.
    if (dt > 500) return;
    if (Math.abs(dx) < 60) return;
    if (Math.abs(dx) < Math.abs(dy) * 2) return;
    if (dx > 0) goPrev(); else goNext();
  }, [goPrev, goNext]);

  // ── Dossier-pill pulse — flash on newly inferred / confirmed fields
  // (replaces the prior brain-strip pill animation now that the strip
  // is gone). ────────────────────────────────────────────────────────
  // Diff `ctx.field_states` against the prior snapshot every render.
  // Keys that transitioned from empty/skipped/absent → ai_guessed /
  // user_confirmed / user_corrected get added to `freshKeys` and flash
  // the CSS .aa-brain-pill--fresh animation (1.4s). The set clears on
  // a short timeout so subsequent turns get their own flash, not a
  // perpetual highlight.
  const [freshKeys, setFreshKeys] = useState<string[]>([]);
  const prevFieldStatesRef = useRef<CompanyContext['field_states'] | null>(null);
  useEffect(() => {
    const prev = prevFieldStatesRef.current;
    const next = ctx.field_states ?? {};
    prevFieldStatesRef.current = next;
    if (!prev) return; // first hydration — no pulse on initial load
    const newlyFilled: string[] = [];
    for (const [k, entry] of Object.entries(next)) {
      if (k.startsWith('_')) continue;
      const st = entry?.state;
      if (st !== 'ai_guessed' && st !== 'user_confirmed' && st !== 'user_corrected') continue;
      const prevEntry = prev[k];
      const prevSt = prevEntry?.state;
      // Only pulse on transitions FROM not-filled. Confirmed-then-edited
      // pulses too because the state changes (confirmed → corrected).
      if (!prevEntry || prevSt === 'empty' || prevSt === 'skipped' || prevSt !== st) {
        newlyFilled.push(k);
      }
    }
    if (newlyFilled.length === 0) return;
    setFreshKeys(newlyFilled);
    const handle = window.setTimeout(() => setFreshKeys([]), 1500);
    return () => window.clearTimeout(handle);
  }, [ctx.field_states]);

  // Derive the section tag string ("COMPANY · A") from a field key like
  // "a.headcount". Reads from `sections` which is loaded with the schema.
  const sectionTagFor = useCallback((fieldKey: string | null | undefined): string | null => {
    if (!fieldKey) return null;
    const letter = fieldKey.split('.')[0] as SectionLetter;
    const name = sections[letter];
    if (!name) return null;
    return `${name} · ${letter.toUpperCase()}`;
  }, [sections]);

  // Central next-question → turn pusher. Pushes a normal orbit turn
  // carrying `phase` (so the turn renders its "Phase N of 3" banner). The
  // enter_problem_scoping → problem_canvas hop is handled inside pushTurn
  // itself, so this just forwards the directive and pushTurn redirects it.
  const pushNextQ = useCallback((nq: NextQuestion | null | undefined): boolean => {
    if (!nq) return false;
    if (nq.prompt || nq.directive_type) {
      pushTurn({
        role: 'orbit',
        text: nq.prompt || '',
        field: nq.field,
        why: nq.why,
        chips: chipsWithSkipMaybe(nq),
        active: true,
        sectionTag: sectionTagFor(nq.field),
        directiveType: nq.directive_type ?? null,
        directivePayload: nq.directive_payload ?? null,
        phase: nq.phase ?? null,
      });
      return true;
    }
    return false;
  }, [pushTurn, chipsWithSkipMaybe, sectionTagFor]);

  // Persist turns whenever they change so reloads restore the conversation.
  // Capped at the last 200 turns to stay well under localStorage limits.
  useEffect(() => {
    if (!clientId) return;
    try {
      const slice = turns.slice(-200);
      window.localStorage.setItem(`${TURNS_STORAGE_KEY}:${clientId}`, JSON.stringify(slice));
    } catch {
      // Storage full / private mode — drop silently.
    }
  }, [turns, clientId]);

  // Host refresh hook (panel mount) — after each turn, let the host page
  // (My Business) re-pull its scoping-status card so the dashboard stays
  // in sync with the conversation. No-op on the full-page mount.
  // The callback lives in a ref and the effect depends only on turns.length:
  // hosts pass inline arrows (new identity per render), and their refetch
  // setState re-renders them — with onAfterTurn in the deps that fed back
  // into an infinite refetch loop once the conversation had a turn.
  const onAfterTurnRef = useRef(onAfterTurn);
  useEffect(() => { onAfterTurnRef.current = onAfterTurn; });
  useEffect(() => {
    if (turns.length > 0) onAfterTurnRef.current?.();
  }, [turns.length]);

  // Persist the unified UI-state blob whenever any field changes. This
  // covers everything the user sees that ISN'T in turns or ctx:
  // question (next-question shape), suggestion (paid-human nudge),
  // eduDismissed, paidHumanPillOpened, prdDrafting. Reload restores
  // the exact same UI state without waiting for a /converse round-trip.
  useEffect(() => {
    if (!clientId) return;
    try {
      const blob = JSON.stringify({
        question,
        suggestion,
        eduDismissed,
        paidHumanPillOpened,
        prdDrafting,
      });
      window.localStorage.setItem(`${UI_STATE_KEY}:${clientId}`, blob);
    } catch {
      // Storage full / private mode — drop silently.
    }
  }, [clientId, question, suggestion, eduDismissed, paidHumanPillOpened, prdDrafting]);

  // Drift should not live only in the pill. When route_plan detects a
  // selected-vs-recommended mismatch, add one durable chat notice per
  // engagement/path pair so the user understands why Orbit changed course.
  useEffect(() => {
    if (!clientId || !routePlan?.route_drift) return;
    const from = routePlan.selected_approach ?? 'none';
    const to = routePlan.recommended_path ?? 'none';
    const engagement = routePlan.engagement_id ?? 'latest';
    const key = `orbit:drift-announced:${clientId}:${engagement}:${from}->${to}`;
    try {
      if (window.localStorage.getItem(key)) return;
      window.localStorage.setItem(key, '1');
    } catch {
      // Storage unavailable — still announce in this session.
    }
    pushTurn({
      role: 'system',
      kind: 'warn',
      text: routeDriftAnnouncement(routePlan),
    });
  }, [clientId, routePlan, pushTurn]);

  // We surface neither the recommendation nor the post-pick CTA in the
  // chat scroll — both live in the welded RecommendationPill above the
  // composer (recommend mode pre-pick, resume mode post-pick). The chat
  // stays focused on conversation, not action cards.


  const applyConverseResponse = useCallback((r: {
    next_question: NextQuestion | null;
    suggestion?: SuggestionResult | null;
    completion: { captured: number; total: number };
    locked: boolean;
    route_plan?: RoutePlan | null;
    orbit_reply?: string | null;
    orbit_session?: OrbitSession | null;
    // v0.2 — agent-committed route_to. When set, mirror the user-pick
    // flow (call updateApproach) so the engagement row + downstream
    // side-effects (consultant assign, route_plan recompute, lane card
    // flip on /my-business) fire through the existing path.
    route_to?: string | null;
    route_reason?: string | null;
  }) => {
    // Conversation-only turns: keep the active question pinned. The
    // backend returns the SAME field in next_question. Refresh `question`
    // state when next_question carries a real prompt — handleChipSelect
    // reads this to know which field the next chip click belongs to.
    // When next_question is null (a defensive backend return), preserve
    // existing question state instead of wiping it.
    if (!r.orbit_reply) {
      setQuestion(r.next_question);
    } else if (r.next_question?.prompt && r.next_question.field) {
      setQuestion(r.next_question);
    }
    if (r.suggestion !== undefined) setSuggestion(r.suggestion);
    setCounts({ captured: r.completion.captured, total: r.completion.total });
    setLocked(r.locked);
    if (r.route_plan !== undefined) setRoutePlan(r.route_plan ?? null);
    if (r.orbit_session !== undefined) setOrbitSession(r.orbit_session ?? null);

    // v0.2 — fire the route commit if the agent emitted one. We call
    // /intake/approach directly (same write path the user-pick flow
    // uses). Best-effort — failures don't block the chat; the user can
    // still pick manually from RecommendationPill / ApproachChoice.
    // No follow-up refreshState here: the caller's `await refreshState`
    // before applyConverseResponse has already pulled the latest, and
    // the lane card on /my-business reads its own state on mount.
    if (r.route_to && (r.route_to === 'automated' || r.route_to === 'paid_human' || r.route_to === 'explore')) {
      const approach = r.route_to as Approach;
      updateApproach(approach).catch((e) => {
        console.error('[orbit] route_to commit failed', e);
      });
    }
  }, []);

  // ── Opener — fires once after /state lands so we know whether this is
  // a returning user (dossier has user_confirmed fields) or cold-start.
  //
  // Returning user → skip the opener and call /converse with empty input
  //                  so Orbit picks up where they left off naturally.
  // Cold start + corporate email → suggest scanning the email's domain.
  // Cold start + personal email → generic invitation.
  //
  // Ref guard so React StrictMode's double-mount doesn't double-seed.
  // Server-side conversation thread + last asked field, captured from
  // /state. On reload we rebuild the EXACT turns from this (questions
  // asked + answers given) instead of re-running Orbit — so the pane
  // survives localStorage loss / client_id changes. Refs (not state) so
  // the opener effect reads the latest without re-running on every change.
  const serverThreadRef = useRef<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const lastInquiredFieldRef = useRef<string | null>(null);

  const seededOpenerRef = useRef(false);
  const deepScopingResumeRef = useRef(false);
  useEffect(() => {
    if (seededOpenerRef.current) return;
    if (!bootstrapReady) return;

    // Returning from a legacy deep-scoping checkout (?deep_scoping= in the
    // URL): a dedicated effect below resumes the walk. Don't run the normal
    // cold/resume path, or it'll fight the resume and push a stray question.
    if (new URLSearchParams(window.location.search).get('deep_scoping')) {
      seededOpenerRef.current = true;
      return;
    }

    seededOpenerRef.current = true;

    // Turns hydrated from localStorage → conversation already exists, just
    // render it. No greeting, no resume converse, no duplicate question.
    if (turns.length > 0) return;

    // Fresh "Scope a problem" → the opener (seeded in the turns initializer)
    // IS the start. NEVER auto-resume the shared dossier slot here, or we'd
    // surface the previous problem's next question instead of asking for the
    // new problem. The first answer sends new_problem=true to clear the slot.
    if (freshProblem) return;

    // Returning user fallback — server has fields the USER engaged with
    // (dossier 23/49 visible) but localStorage is empty (new browser,
    // cleared cache, incognito). Skip the cold-start chooser and call
    // /converse with empty input so Orbit picks up the next pending
    // question — feels seamless, not like the system forgot them.
    //
    // CRITICAL: count only USER-TOUCHED fields (user_confirmed /
    // user_corrected). `ai_guessed` must NOT count here. A brand-new
    // Google signup arrives with company name + website auto-derived
    // from the email domain and written as `ai_guessed` (see
    // intake.py _apply_derivations) — so it's never truly at 0/N. If
    // ai_guessed counted, that user would skip the landing chooser and
    // get dropped straight into questions ("Organization type?") having
    // never chosen a path. The chooser is the landing until the user
    // actually engages.
    const captured = Object.values(ctx.field_states || {}).filter((fs) => {
      const st = (fs as { state?: string })?.state;
      return st === 'user_confirmed' || st === 'user_corrected';
    }).length;

    // ── EXACT-STATE REBUILD from the server thread ────────────────────
    // Reload must restore the REAL questions asked + answers given — not
    // re-run Orbit to regenerate a question (the old resume-converse,
    // which collapsed the pane to 1 turn). The server stores the
    // conversation (agent_thread); rebuild the pane from it and restore
    // the active, answerable question from last_inquired_field. No resume.
    // Rebuild from the server thread ONLY when the user actually engaged —
    // i.e. the thread carries a user message, OR a field is user-touched.
    // An assistant-ONLY thread is a stale auto-opener (the intake agent's
    // "Hey, I'm Orbit… what are you looking to build?" + first question)
    // that a prior session generated before the user ever picked a path —
    // historically via the buggy ai_guessed resume. Rebuilding that on
    // reload buries the landing chooser. No user turn, no user-touched
    // field → not engagement → fall through to the cold-start chooser.
    const serverThread = serverThreadRef.current || [];
    const threadHasUserMsg = serverThread.some((m) => m.role === 'user');
    // Rebuild from the server thread when the user engaged on THIS context
    // (client-wide or per-problem — agent_thread is stored per problem card).
    if (serverThread.length > 0 && (captured > 0 || threadHasUserMsg)) {
      const activeField = lastInquiredFieldRef.current;
      const fieldDef = activeField ? fields.find((f) => f.key === activeField) : undefined;
      const fieldChips = (fieldDef?.options || []) as NextQuestion['chips'];
      let lastAssistantIdx = -1;
      for (let i = serverThread.length - 1; i >= 0; i--) {
        if (serverThread[i].role === 'assistant') { lastAssistantIdx = i; break; }
      }
      serverThread.forEach((m, i) => {
        if (m.role === 'user') {
          pushTurn({ role: 'user', text: m.content });
        } else if (i === lastAssistantIdx && fieldDef && activeField) {
          // The current question — restore it answerable (exact text +
          // the field's chips), so the user picks up exactly where they
          // left off.
          pushTurn({
            role: 'orbit',
            text: '',
            reply: m.content,
            field: activeField,
            why: fieldDef.why ?? null,
            chips: fieldChips,
            active: true,
            sectionTag: sectionTagFor(activeField),
          });
        } else {
          pushTurn({ role: 'orbit', text: m.content });
        }
      });
      if (fieldDef && activeField) {
        setQuestion({
          field: activeField,
          label: fieldDef.label,
          prompt: fieldDef.label,
          why: fieldDef.why ?? null,
          kind: fieldDef.kind,
          chips: fieldChips ?? [],
          placeholder: fieldDef.placeholder ?? null,
          is_confirming_guess: false,
          current_guess: null,
        });
      }
      return; // exact state restored — never resume-regenerate
    }

    // Resume via empty-input /converse when EITHER the user has engaged before
    // (captured > 0) OR this is a per-problem open (`_ctxProblemId`) — clicking
    // "Scope with Orbit" on an existing problem must resume THAT problem and ask
    // its next question, never drop into the cold-start "what problem?" opener
    // (the bug: a seed/voice-created problem with no chat history fell through
    // to cold start). The problem is already checked out into the slot.
    // COMPANY mount (My Business) — do NOT run the generic resume here. It
    // fires converse('') off the backend's persisted route + section_e, which
    // still holds the LAST PROBLEM → it would resume the problem and pollute
    // the company thread (the "I clicked Map your business but it shows my last
    // problem" bug). The drawer fires `orbit:route {company}` on every open, and
    // handleRoutePick('company') is the SOLE owner of the company resume — it
    // queries the backend company-scoped and shows what's mapped + the next
    // field. So stand down here for company and let that own it.
    if ((captured > 0 || _ctxProblemId) && !isCompanyRoute) {
      // Quiet system note + immediate /converse resume. Empty user
      // input → Orbit's planner picks the next highest-leverage open
      // field and asks it. No artificial greeting — the seamless
      // resume IS the welcome back.
      pushTurn({
        role: 'system',
        kind: 'info',
        text: _ctxProblemId ? 'Picking up this problem where you left off.' : 'Picking up where you left off.',
      });
      (async () => {
        try {
          setPending(true);
          await refreshState();
          // streamTurn (not plain converse) so the resume streams its recap +
          // any reply into the thinking bubble — the "we present to them"
          // trigger. A bare converse() discards those SSE events.
          const r = await streamTurn('', null, null, _converseOpts());
          _adoptProblem(r);
          applyConverseResponse(r);
          // applyConverseResponse updates state (question, counts, etc.)
          // but does NOT push a turn — sendText / sendUrl / etc. push
          // the turn themselves after calling it. Resume flow does the
          // same here: a compound_chips card if the planner batched
          // siblings, else the single next_question as an orbit turn.
          if (COMPOUND_CHIPS_ENABLED && r.compound_chips && r.compound_chips.fields.length >= 2) {
            pushTurn({ role: 'compound_chips', payload: r.compound_chips });
          } else if (r.next_question?.prompt || r.next_question?.directive_type) {
            pushTurn({
              role: 'orbit',
              text: r.next_question.prompt || '',
              field: r.next_question.field,
              why: r.next_question.why,
              chips: chipsWithSkipMaybe(r.next_question),
              active: true,
              sectionTag: sectionTagFor(r.next_question.field),
              directiveType: r.next_question.directive_type ?? null,
              directivePayload: r.next_question.directive_payload ?? null,
            });
          } else {
            // No next question (intake might be complete). Surface a
            // soft welcome-back so the user lands on something, not
            // on a bare system note.
            pushTurn({
              role: 'orbit',
              text: "Welcome back. Your dossier's on the right — anything you'd like to update or push forward?",
              why: "I'll pick the next thread from whatever you share.",
              active: true,
            });
          }
        } catch (e) {
          console.error('[orbit] resume converse failed', e);
          // Fallback to opener if resume fails so the user isn't
          // stuck on a system note with no question to answer.
          pushTurn({
            role: 'orbit',
            text: (
              "Welcome back. Where would you like to pick up — "
              + "more about your business, the problem you're scoping, "
              + "or the engagement shape?"
            ),
            why: "Your dossier is on the right; we'll thread from here.",
            active: true,
          });
        } finally {
          setPending(false);
        }
      })();
      return;
    }

    // Cold-start opener — ONE orbit message, and ONLY that. We used to
    // also fire /email-hints here and push a BatchConfirmCard with
    // domain-inferred fields. That card landed AFTER the welcome (async),
    // pinned the pane to the welcome while the card sat at turn 2, and
    // the composer disabled itself ("viewing a past turn") — so the user
    // literally couldn't type on landing. The prefill nicety wasn't worth
    // a broken first impression. The welcome now stands alone: it's the
    // last turn, the composer is live, the user types immediately.
    //
    // Email-domain inference still runs server-side (fields land as
    // ai_guessed in the dossier rail); we just don't shove a confirm
    // card in front of the welcome.
    const _coldStartEmail = (() => {
      try {
        return (window.localStorage.getItem('user_email') || '').trim();
      } catch {
        return '';
      }
    })();
    if (corporateDomain && _coldStartEmail) {
      // Warm the inference silently — no card push, no pane disruption.
      fetchEmailHints(_coldStartEmail, clientId ?? undefined)
        .then(() => { refreshState().catch(() => {}); })
        .catch(() => { /* silent — the welcome stands regardless */ });
    }

    // Single opener — same copy whether email is corporate or personal.
    //
    // Voice notes:
    //   1. Frame Orbit's job FIRST so the user knows the time is well
    //      spent ("before any code gets written"). Lowers the bar to
    //      sharing — they're not pitching, they're scoping.
    //   2. Open the question with three concrete starting points so
    //      the user doesn't have to translate "what are you trying to
    //      fix" into a structured answer. Permission to start anywhere.
    //   3. Why I ask normalizes uncertainty ("any starting point
    //      works") instead of selling the multi-modal affordances —
    //      the composer icons (paperclip, link, mic) already do that.
    //   4. Brand voice: sentence case, em-dash for rhythm, no
    //      exclamations, no emoji, period-ended.
    // Pin the pane to this welcome until the user acts — so the async
    // email-prefill card (pushed above) can't snap the pane forward and
    // bury the first impression. The welcome is always index 0 here
    // (this branch only runs when turns.length === 0), so point the
    // pane at it explicitly — the auto-advance effect is suppressed
    // while the hold is active.
    // A1 — ask for something to read BEFORE the chooser. The chooser used to
    // be the landing, which meant we asked people to pick a path before we
    // knew anything about them, and only asked for the website later, inside
    // the business walk. Reading first is what lets the cards that follow open
    // already filled in. `handleLandingRead` advances to the chooser once the
    // read returns; `handleLandingSkip` goes straight there.
    holdWelcomeRef.current = true;
    setActiveTurnIdx(0);
    // ...unless we already have something to read.
    //
    // The company route deliberately stands down from the resume above (see the
    // comment there), so this cold-start branch runs on EVERY open of Map My
    // Business. It asked for the website every time, including when the website
    // was already on file or had been explicitly declined, which reads as the
    // product forgetting what it was just told. A settled answer, either a real
    // address or "we don't have one", means this question is done.
    //
    // A website that is on file but only GUESSED (a Solution Partner typed it
    // during Add a client, or a read inferred it) is not settled: the question
    // is asked once more with that address already in the field, so the owner
    // confirms it rather than typing it again.
    const _websiteValue = ctx.section_a?.website;
    const _websiteState = (ctx.field_states || {})['a.website']?.state;
    const _websiteSettled = (
      _websiteState === 'user_confirmed'
      || _websiteState === 'user_corrected'
      || _websiteState === 'skipped'
    );
    const _websiteGuess = typeof _websiteValue === 'string' ? _websiteValue.trim() : '';
    let opener: Turn = { role: 'website_intake', lastUrl: _websiteGuess || undefined };
    if (_websiteSettled) opener = { role: 'route_chooser' };

    // Read before ask. Facts a Solution Partner typed, or a website or document
    // read, sit in the dossier as guesses. They are confirmed first, in one
    // card, so the chat never asks what is already on file.
    const guessed: BatchConfirmPayload['rows'] = [];
    Object.entries(ctx.field_states || {}).forEach(([key, entry]) => {
      if (!entry || entry.state !== 'ai_guessed') return;
      const [letter, ...rest] = key.split('.');
      const suffix = rest.join('.');
      const sectionKey = `section_${letter}` as keyof CompanyContext;
      const section = ctx[sectionKey] as Record<string, unknown> | undefined;
      const value = section ? section[suffix] : undefined;
      if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) return;
      const def = fields.find((f) => f.key === key);
      if (!def) return;
      let display = String(value);
      if (Array.isArray(value)) display = value.map((v) => String(v)).join(', ');
      guessed.push({ field: key, label: def.label, value, value_display: display, confidence: Number(entry.confidence ?? 0.8), section: letter });
    });
    if (guessed.length >= 1) {
      roomOpenerRef.current = opener;
      pushTurn({
        role: 'batch_confirm',
        payload: { source: 'room', source_label: 'your Solution Partner and what Orbit already read', rows: guessed, title: 'Here is what I already know about your company. Right?' },
      });
      return;
    }
    pushTurn(opener);
  }, [bootstrapReady, turns.length, ctx.section_a, ctx.field_states, corporateDomain, clientId, applyConverseResponse, pushTurn]);

  // ── Bootstrap (no auto-question on cold start) ────────────────────
  useEffect(() => {
    let cancelled = false;

    const loadSchema = async () => {
      try {
        const s = await fetchSchema(undefined, clientId ?? undefined);
        if (cancelled) return;
        setFields(s.fields);
        setSections(s.sections);
      } catch (e) {
        console.error('[orbit] /schema failed', e);
      }
    };

    const loadState = async () => {
      if (!clientId) return;
      const stateProblemId = _ctxProblemId ?? activeProblemIdRef.current;
      try {
        const s = await fetchState(clientId, stateProblemId);
        if (cancelled) return;
        // Capture the server conversation thread for reload rebuild.
        serverThreadRef.current = Array.isArray(s.agent_thread) ? s.agent_thread : [];
        lastInquiredFieldRef.current = s.last_inquired_field ?? null;
        setCtx({
          section_a: s.company_context.section_a as Record<string, unknown>,
          section_b: s.company_context.section_b as Record<string, unknown>,
          section_c: s.company_context.section_c as Record<string, unknown>,
          section_d: s.company_context.section_d as Record<string, unknown>,
          section_e: s.company_context.section_e as Record<string, unknown>,
          section_f: s.company_context.section_f as Record<string, unknown>,
          section_g: s.company_context.section_g as Record<string, unknown>,
          field_states: s.company_context.field_states,
          completion_pct: s.company_context.completion_pct,
        });
        setRoutePlan(s.route_plan ?? null);
        // Counter populates from /state — no Sonnet call yet. Preserve
        // total if the schema effect has already set it; otherwise leave
        // it at 0 and the schema effect will fill it shortly. Never
        // clobber with a hard 0 here (race with schema fetch).
        const captured = Object.values(s.company_context.field_states || {})
          .filter((fs) => {
            const st = (fs as { state?: string })?.state;
            return st === 'user_confirmed' || st === 'user_corrected' || st === 'ai_guessed';
          }).length;
        setCounts(prev => ({ captured, total: prev.total }));
      } catch (e) {
        console.error('[orbit] /state failed', e);
      }
    };

    (async () => {
      // Fresh "Scope a problem" → clear the shared working slot on the
      // backend BEFORE hydrating, so /state returns a clean section_e and we
      // never resume the previously-scoped problem (its data is safe on its
      // own problem_card). Must complete before loadState reads /state.
      if (freshProblem && clientId) {
        try { await newProblemReset(clientId); } catch { /* best effort */ }
      } else if (_ctxProblemId && clientId) {
        // Resuming a SPECIFIC problem (dashboard per-problem panel): check it
        // out so /state below returns THAT problem's captured fields + next
        // question, not whatever was last in the shared slot.
        try { await resumeProblem(_ctxProblemId, clientId); } catch { /* best effort */ }
      }
      await Promise.all([loadSchema(), loadState()]);
      if (!cancelled) setBootstrapReady(true);
    })();

    return () => { cancelled = true; };
  }, [clientId]);

  // Update counts.total once fields land (visible field count is what matters)
  useEffect(() => {
    setCounts(prev => ({ ...prev, total: fields.length }));
  }, [fields.length]);

  // ── Refresh from /state ───────────────────────────────────────────
  const refreshState = useCallback(async () => {
    if (!clientId) return null;
    const stateProblemId = activeProblemIdRef.current ?? _ctxProblemId;
    try {
      const s = await fetchState(clientId, stateProblemId);
      serverThreadRef.current = Array.isArray(s.agent_thread) ? s.agent_thread : [];
      lastInquiredFieldRef.current = s.last_inquired_field ?? null;
      setCtx({
        section_a: s.company_context.section_a as Record<string, unknown>,
        section_b: s.company_context.section_b as Record<string, unknown>,
        section_c: s.company_context.section_c as Record<string, unknown>,
        section_d: s.company_context.section_d as Record<string, unknown>,
        section_e: s.company_context.section_e as Record<string, unknown>,
        section_f: s.company_context.section_f as Record<string, unknown>,
        section_g: s.company_context.section_g as Record<string, unknown>,
        field_states: s.company_context.field_states,
        completion_pct: s.company_context.completion_pct,
      });
      setRoutePlan(s.route_plan ?? null);
      return s;
    } catch (e) {
      console.error('[orbit] refresh /state failed', e);
      return null;
    }
  }, [clientId, _ctxProblemId]);

  // Slice 4 — BatchConfirmCard submit handler. The card already wrote
  // the per-row confirms via /api/intake/confirm-batch and got back the
  // standard next_question + completion + suggestion bundle. This
  // handler just threads that response into the chat: mark the turn as
  // submitted (so its rendering goes terminal), refresh dossier state,
  // push the next question, and update counts/suggestion/route_plan.
  // Slice 4 — AnythingElseCard handlers. On submit, the card has
  // already piped the user's prose through paste-ingest. If that
  // ingest dropped ≥2 ai_guessed deltas, push a BatchConfirmCard so
  // the user can review the new fields in one tap. Skip just freezes
  // the turn.
  const handleAnythingElseSubmitted = useCallback(
    (
      turnIndex: number,
      _ingestResp: IngestResponse,
      batchConfirm: BatchConfirmPayload | null,
    ) => {
      setTurns(prev => prev.map((t, i) => (
        i === turnIndex && t.role === 'anything_else'
          ? { ...t, submitted: true }
          : t
      )));
      refreshState().catch(() => {});
      if (batchConfirm && batchConfirm.rows.length >= 2) {
        pushTurn({ role: 'batch_confirm', payload: batchConfirm });
      }
    },
    [refreshState, pushTurn],
  );

  const handleAnythingElseSkipped = useCallback(
    (turnIndex: number) => {
      setTurns(prev => prev.map((t, i) => (
        i === turnIndex && t.role === 'anything_else'
          ? { ...t, submitted: true }
          : t
      )));
    },
    [],
  );

  const handleBatchConfirmSubmitted = useCallback(
    (
      turnIndex: number,
      res: BatchConfirmResponse,
      payload?: BatchConfirmPayload | null,
    ) => {
      releaseWelcomeHold();
      setTurns(prev => prev.map((t, i) => {
        if (i !== turnIndex) return t;
        if (t.role === 'batch_confirm' || t.role === 'compound_chips') {
          return { ...t, submitted: true };
        }
        return t;
      }));
      refreshState().catch(() => {});
      if (payload?.source === 'room') {
        const opener = roomOpenerRef.current;
        roomOpenerRef.current = null;
        if (opener) pushTurn(opener);
        return;
      }
      applyConverseResponse({
        next_question: res.next_question,
        suggestion: res.suggestion,
        completion: res.completion,
        locked: res.locked,
        route_plan: res.route_plan,
      });

      // When the user confirmed an email-prefill card AND kept the
      // `a.website` row, fire the Firecrawl URL ingest in the
      // background — the CTA copy already promised this ("Looks right
      // — read brndsh.io"). Push a soft system line so they see the
      // scan happening, then a new BatchConfirmCard with whatever the
      // deeper crawl pulled. Failure surfaces as a warn line.
      if (payload?.source === 'email_domain') {
        const websiteRow = payload.rows.find(r => r.field === 'a.website');
        const websiteKept =
          websiteRow
          && !res.skipped.includes('a.website')
          && (res.confirmed.includes('a.website') || res.edited.includes('a.website'));
        if (websiteRow && websiteKept) {
          const rawUrl = String(websiteRow.value || '');
          let host = rawUrl;
          try { host = new URL(rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`).host || rawUrl; } catch {}
          // The user approved the site. Reading it can take a while, so show a
          // live loading state (the url_reading pulse), NOT a one-off line.
          // When it's done we do NOT tell the user what we pulled — the dossier
          // rail fills silently — Orbit just asks the next company question and
          // keeps moving along with them. The pulled fields surface in the rail
          // for anyone who wants to look; we don't interrupt to recap them.
          pushTurn({ role: 'url_reading', host });
          const dropReading = () =>
            setTurns(prev => prev.filter(t => t.role !== 'url_reading'));
          // Ask the next thoughtful question once the scan settles. Empty
          // converse → the Inquirer ranks remaining fields and returns the next
          // one (Section A fields the crawl didn't fill, then onward). Runs even
          // on scan failure so the flow never stalls.
          const askNext = async () => {
            try {
              const c = await converse('', null, clientId ?? undefined, null, undefined, _converseOpts());
              _adoptProblem(c);
              if (c.next_question?.prompt || c.next_question?.directive_type) {
                pushTurn({
                  role: 'orbit',
                  text: c.next_question.prompt || '',
                  field: c.next_question.field,
                  why: c.next_question.why,
                  chips: chipsWithSkipMaybe(c.next_question),
                  active: true,
                  sectionTag: sectionTagFor(c.next_question.field),
                  directiveType: c.next_question.directive_type ?? null,
                  directivePayload: c.next_question.directive_payload ?? null,
                });
              }
              applyConverseResponse(c);
            } catch (e) {
              console.warn('[orbit] company-route next-question failed', e);
            }
          };
          setPending(true);
          ingest('url', rawUrl, clientId ?? undefined)
            .then(() => refreshState().catch(() => {}))
            .catch((e) => { console.warn('[orbit] company-route scan failed', e); })
            .finally(() => { dropReading(); askNext().finally(() => setPending(false)); });
          return;
        }
      }

      // Slice 4 — if the post-batch next-question has compound siblings,
      // chain straight into another CompoundChipCard instead of one
      // single-question turn. Keeps the user in batched flow as long as
      // batchable sections remain.
      if (COMPOUND_CHIPS_ENABLED && res.compound_chips && res.compound_chips.fields.length >= 2) {
        pushTurn({ role: 'compound_chips', payload: res.compound_chips });
        return;
      }
      if (res.next_question?.prompt || res.next_question?.directive_type) {
        pushTurn({
          role: 'orbit',
          text: res.next_question.prompt || '',
          field: res.next_question.field,
          why: res.next_question.why,
          chips: chipsWithSkipMaybe(res.next_question),
          active: true,
          sectionTag: sectionTagFor(res.next_question.field),
          directiveType: res.next_question.directive_type ?? null,
          directivePayload: res.next_question.directive_payload ?? null,
        });
      }
    },
    [clientId, refreshState, applyConverseResponse, pushTurn, sectionTagFor],
  );

  // If the user returns with chat turns restored from localStorage, the
  // visible question card may exist before React's `question` state is
  // hydrated. Recover it from the last question turn so free-text replies
  // still carry the active field to the backend.
  const recoveredQuestion = useMemo(
    () => question ? null : recoverActiveQuestion(turns, fields),
    [question, turns, fields],
  );
  const activeQuestion = question ?? recoveredQuestion;

  // Grounded status shown under the thinking dots so the user knows what
  // Orbit is working on during the wait — derived from the active field's
  // section (problem / company / tools / a Phase). Honest + compact, no
  // fake step narration.
  const thinkingStatus = useMemo(() => {
    // The generic "what Orbit is working on" label, derived from the active
    // field's section. The live SSE "understood" line + streaming reply now
    // render through OrbitThinking's own props; this is just the pre-stream
    // placeholder shown for the first beat before anything streams back.
    const f = (activeQuestion?.field || '').split('.')[0];
    const map: Record<string, string> = {
      a: 'Your company', b: 'Your tools', c: 'Your company',
      d: 'Your goals', e: 'Scoping your problem', f: 'Budget',
      g: 'Engagement',
      h: 'Phase 1 · company shell', i: 'Phase 1 · tools',
      m: 'Phase 1 · procurement', n: 'Phase 1 · buying committee',
      o: 'Phase 1 · compliance', k: 'Phase 3 · outcomes',
      p: 'Phase 3 · scorecard',
    };
    return map[f] ?? null;
  }, [activeQuestion]);

  useEffect(() => {
    if (!question && recoveredQuestion) {
      setQuestion(recoveredQuestion);
    }
  }, [question, recoveredQuestion]);

  // Drop-in for converse() that STREAMS the turn over SSE: surfaces the live
  // "understood" line in the thinking indicator and keeps the connection
  // producing bytes so a slow turn never trips the prod 499. Returns the full
  // ConverseResponse on `done`, so every caller's post-processing is unchanged.
  // Falls back to the blocking endpoint ONLY when the stream never established
  // (HTTP error / no body) — never after events, so a turn that already ran
  // server-side is never double-processed.
  const streamTurn = useCallback(async (
    input: string,
    field: string | null,
    shown: ShownQuestionPayload | null | undefined,
    opts: ConverseOptions | undefined,
  ): Promise<ConverseResponse> => {
    let finalResult: ConverseResponse | null = null;
    let firstEvent = false;
    // Fresh canvas for this turn's live render.
    setStreamAck(null);
    setStreamReply('');
    try {
      await converseStream(input, field, clientId ?? undefined, shown, undefined, opts, {
        onThinking: () => { firstEvent = true; },
        onUnderstood: (ack) => { firstEvent = true; if (ack) setStreamAck(ack); },
        onReply: (chunk) => { firstEvent = true; if (chunk) setStreamReply(prev => prev + chunk); },
        onDone: (result) => { finalResult = result; },
        onError: () => { /* backend turn failed — surfaced via the throw below */ },
      });
    } catch (e) {
      if (!firstEvent) {
        // Never established → the streamed turn didn't run; safe to fall back.
        return await converse(input, field, clientId ?? undefined, shown, undefined, opts);
      }
      throw e; // mid-stream drop → turn ran server-side; don't double-run.
    }
    if (finalResult) return finalResult;
    throw new Error('converse stream ended without a result');
  }, [clientId]);

  // ── User text / chip pick → /converse ─────────────────────────────
  const sendText = useCallback(async (text: string) => {
    releaseWelcomeHold();
    // Derive shown_question from the last rendered orbit turn (registry
    // question OR cold-start hero). This is the conversation context
    // the agent reads so "yes" / "that's not us" land against what
    // Orbit just asked. Without it, confirmations are blind.
    const priorShown = shownQuestionFromHistory(turns, activeQuestion);
    pushTurn({ role: 'user', text });
    setPending(true);

    // Listen for help-rejection patterns and dismiss the pill INSTANTLY
    // so the user feels heard before the /converse roundtrip (~5-10s).
    // Backend agent will also extract clarity_self_signal=deep +
    // help_level=self_serve via the prompt rule, which removes the
    // pill permanently on the next state hydration. This is just the
    // immediate-feedback path — without it, the user sees their
    // rejection ignored for a full request cycle.
    if (HELP_REJECTION_PATTERN.test(text)) {
      setPaidHumanPillOpened(true);
    }
    try {
      const r = await streamTurn(
        text,
        activeQuestion?.field ?? null,
        priorShown,
        _converseOpts(),
      );
      _adoptProblem(r);

      // Slice 1 — path-switch narration. When Orbit's read_user_emphasis
      // declared a different path than last turn, drop a one-line system
      // turn ABOVE the action narrations + orbit_reply so the user feels
      // the focus shift, not just sees the dossier reorder silently.
      if (r.path_just_switched && r.path) {
        const PATH_SWITCH_LINE: Record<'problem' | 'company' | 'explore', string> = {
          problem: "Sounds like there's a specific problem here. I'll focus on that.",
          company: 'Let me get to know your company first.',
          explore: "Got it — let me show you what's possible.",
        };
        pushTurn({
          role: 'system',
          text: PATH_SWITCH_LINE[r.path],
          kind: 'info',
        });
      }

      // SAFETY NET: a turn must never leave the chat blank. Mirror every
      // branch below — if none of them would render anything (no reply, no
      // reframe, no compound chips, no anything-else, no ack, and no
      // next_question prompt/directive), the screen would go dead and the
      // user's message would sit alone with no response. This happens at
      // terminal/committed states (problem PRD-done, company mapped, L2
      // complete) where the backend has nothing left to ask. Backend now
      // surfaces orbit_reply in that case; this is the belt-and-braces guard.
      const willRender =
        !!r.orbit_reply
        || !!r.reframe
        || !!(COMPOUND_CHIPS_ENABLED && r.compound_chips && r.compound_chips.fields.length >= 2)
        || !!r.anything_else
        || !!r.ack
        || !!(r.next_question?.prompt || r.next_question?.directive_type);

      // Orbit replied conversationally (user asked a question, made a
      // request, challenged the ask, or wandered off-topic). The backend
      // returns the SAME active field — never push a new question turn,
      // never replace what's on screen. Just drop Orbit's reply into the
      // chat under the unchanged active question.
      //
      // If the agent ran any tools this turn (scan_url, recall, defer)
      // surface them as system turns BETWEEN the user message and the
      // reply so the user sees what Orbit actually did, not just heard.
      if (r.orbit_reply) {
        if (r.actions && r.actions.length > 0) {
          for (const action of r.actions) {
            if (action.narration) {
              pushTurn({
                role: 'system',
                text: action.narration,
                kind: action.ok ? 'success' : 'warn',
              });
            }
          }
        }
        // RULE: if Orbit spoke (orbit_reply), the user MUST see it. The
        // reply already contains any re-asked question (e.g. "…Still on
        // clarity. How clear is the problem?"), so we attach it as `reply`
        // on the question turn and the render shows the reply AS the card
        // text — never the bare registry prompt (which would duplicate the
        // question). The chips below let the user answer.
        const nq = r.next_question;
        const directive = nq?.directive_type ?? null;
        if (directive) {
          // Major directive card — reply is the lead-in above it.
          pushTurn({ role: 'orbit', text: r.orbit_reply });
          pushTurn({
            role: 'orbit',
            text: nq?.prompt || '',
            field: nq?.field ?? null,
            why: nq?.why ?? null,
            chips: nq ? chipsWithSkipMaybe(nq) : undefined,
            active: true,
            sectionTag: sectionTagFor(nq?.field),
            directiveType: directive,
            directivePayload: nq?.directive_payload ?? null,
            phase: nq?.phase ?? null,
          });
        } else {
          pushTurn({
            role: 'orbit',
            text: nq?.prompt || '',     // fallback; `reply` wins in render
            reply: r.orbit_reply,        // the spoken answer (incl. re-ask)
            field: nq?.field ?? null,
            why: nq?.why ?? null,
            chips: nq ? chipsWithSkipMaybe(nq) : undefined,
            active: true,
            sectionTag: sectionTagFor(nq?.field),
            phase: nq?.phase ?? null,
          });
        }
      } else if (r.reframe) {
        pushTurn({ role: 'orbit', text: r.reframe, chips: r.next_question?.chips });
      } else if (COMPOUND_CHIPS_ENABLED && r.compound_chips && r.compound_chips.fields.length >= 2) {
        // Slice 4 — Sharp-fill batching. When the backend bundled the
        // primary next-question with 1-3 same-section chip siblings,
        // skip the per-row turn pushes and drop ONE multi-row card.
        // The card fires confirm-batch on submit and the response
        // carries the follow-up next_question.
        if (r.ack) pushTurn({ role: 'orbit', text: r.ack });
        pushTurn({ role: 'compound_chips', payload: r.compound_chips });
      } else if (r.anything_else) {
        // Section-boundary "anything else?" — render as a PLAIN Orbit
        // question, NOT a card with its own textarea. The card
        // duplicated the composer input that's already at the bottom of
        // the pane (confusing + redundant). Now it's just a question;
        // the user answers in the one composer, and their prose flows
        // through the normal /converse extraction.
        if (r.ack) pushTurn({ role: 'orbit', text: r.ack });
        pushTurn({
          role: 'orbit',
          text: r.anything_else.prompt,
          why: "A sentence or two is plenty — I'll pull anything useful into the dossier. Or just say skip.",
          active: true,
        });
      } else {
        if (r.ack) pushTurn({ role: 'orbit', text: r.ack });
        // Push the turn when there's a prompt OR a directive card. A
        // major directive (propose_prd_draft / enter_l2_self /
        // marketplace_matches) STRIPS the prompt so the card stands
        // alone — so gating only on `prompt` dropped those turns
        // entirely → empty screen, no card. Render the card when
        // directive_type is set even with no prompt text.
        if (r.next_question?.prompt || r.next_question?.directive_type) {
          pushTurn({
            role: 'orbit',
            text: r.next_question.prompt || '',
            field: r.next_question.field,
            why: r.next_question.why,
            chips: chipsWithSkipMaybe(r.next_question),
            active: true,
            sectionTag: sectionTagFor(r.next_question.field),
            directiveType: r.next_question.directive_type ?? null,
            directivePayload: r.next_question.directive_payload ?? null,
          });
        }
      }

      // Belt-and-braces: nothing above rendered. Never leave the user's
      // message hanging with no response. A locked turn means the bucket is
      // fully scoped (nothing left to ask); otherwise treat it as a soft
      // "say more" so the conversation can continue.
      if (!willRender) {
        pushTurn({
          role: 'orbit',
          text: r.locked
            ? "You're all set on this one — I've got what I need. Want to add anything, or should I take it further?"
            : "I'm with you. Tell me a bit more and I'll keep going.",
        });
      }

      // Coaching mode — agent (or the backstop) returned field_suggestions.
      // Attach them INLINE to the latest orbit question turn so they render
      // UNDER the question (the question stays visible) instead of as a
      // separate pane turn that hides it. Composer below still takes free text.
      if (r.field_suggestions && Array.isArray(r.field_suggestions.candidates)
          && r.field_suggestions.candidates.length >= 2) {
        const fsPayload = r.field_suggestions;
        setTurns(prev => {
          for (let i = prev.length - 1; i >= 0; i -= 1) {
            if (prev[i].role === 'orbit') {
              const next = [...prev];
              next[i] = { ...(next[i] as Extract<Turn, { role: 'orbit' }>), fieldSuggestions: fsPayload };
              return next;
            }
          }
          return prev;
        });
      }

      await refreshState();
      applyConverseResponse(r);
    } catch (e) {
      console.error('[orbit] /converse failed', e);
      pushTurn({ role: 'orbit', text: 'Hit a snag on my end. Try sending that again.' });
    } finally {
      setPending(false);
      setStreamAck(null);
      setStreamReply('');
    }
  }, [clientId, activeProblemId, freshProblem, seedMeta, activeQuestion, turns, chipsWithSkipMaybe, sectionTagFor, refreshState, applyConverseResponse, pushTurn, streamTurn]);

  const seedPromptSentRef = useRef(false);
  useEffect(() => {
    const seed = String(seedPrompt || '').trim();
    if (!seed || seedPromptSentRef.current || !bootstrapReady || pending) return;
    seedPromptSentRef.current = true;
    const id = window.setTimeout(() => {
      void sendText(seed);
    }, 250);
    return () => window.clearTimeout(id);
  }, [seedPrompt, bootstrapReady, pending, sendText]);

  // ── URL → Firecrawl extract → /converse to pick up ────────────────
  // Loading proof = dossier on the right rail visibly filling. No system
  // chat messages — we don't talk about what we just did, we just do it.
  // Shared post-ingest flow for URL + doc. Leads with an UNDERSTANDING
  // summary (what Orbit now grasps about the business, in plain language —
  // never "filled N fields"), then asks the next mapping question. The
  // just-guessed fields stay confirmable in the dossier side-panel, so we
  // intentionally do NOT push the field-list batch_confirm card into chat.
  const afterIngest = useCallback(async (
    ingestResp: IngestResponse,
    sharedPhrase: string,
  ) => {
    const summary = (ingestResp.message || '').trim();
    if (summary) pushTurn({ role: 'orbit', text: summary });

    // Converse advances the mapping. When the summary already spoke, take
    // ONLY the structured next_question (skip converse's own reply) so the
    // read is never narrated twice; otherwise let converse both read + ask.
    const seed = summary
      ? `(${sharedPhrase} — you've summed up what you understood. Ask your next business-mapping question.)`
      : `(${sharedPhrase} and the read filled what it could — give a short read on what you understood, then your next question.)`;
    const c = await converse(seed, null, clientId ?? undefined, null, undefined, _converseOpts());
    _adoptProblem(c);
    if (!summary) {
      if (c.ack) pushTurn({ role: 'orbit', text: c.ack });
      if (c.orbit_reply) pushTurn({ role: 'orbit', text: c.orbit_reply });
    }
    if (c.next_question?.prompt || c.next_question?.directive_type) {
      pushTurn({
        role: 'orbit',
        text: c.next_question.prompt || '',
        field: c.next_question.field,
        why: c.next_question.why,
        chips: chipsWithSkipMaybe(c.next_question),
        active: true,
        sectionTag: sectionTagFor(c.next_question.field),
        directiveType: c.next_question.directive_type ?? null,
        directivePayload: c.next_question.directive_payload ?? null,
      });
    }
    applyConverseResponse(c);
  }, [clientId, applyConverseResponse, pushTurn, sectionTagFor]);

  // ── A1: the landing read, ahead of the chooser ────────────────────
  // Same ingest endpoints the composer uses (`ingest` / `ingestFile`), so
  // there is one read pipeline, not two. Only the tail differs: the composer
  // paths hand off to the next interview question, whereas the landing read
  // hands off to the route chooser — the user still has to pick a path, they
  // just do it after we have read something rather than before.
  const advanceToChooser = useCallback(() => {
    // Two things block the pane from moving on its own here. The cold start
    // pins it to the landing (holdWelcomeRef) so the async email-prefill card
    // can't bury the first impression, and that hold suppresses auto-advance.
    // And this transition replaces turns rather than appending, so the array
    // can shrink, while auto-advance only fires on growth.
    releaseWelcomeHold();
    forcePaneToLastRef.current = true;
    setTurns(prev => [
      ...prev.filter(t => t.role !== 'website_intake' && t.role !== 'url_reading'),
      { role: 'route_chooser' },
    ]);
  }, [setTurns, releaseWelcomeHold]);

  const handleLandingSkip = useCallback(() => {
    advanceToChooser();
  }, [advanceToChooser]);

  const handleLandingRead = useCallback(
    async (input: { kind: 'url'; value: string } | { kind: 'text'; value: string } | { kind: 'doc'; file: File }) => {
      // Drop the intake step immediately: it is answered, and leaving it
      // mounted behind the progress card lets a second submit through.
      setTurns(prev => prev.filter(t => t.role !== 'website_intake'));

      let host = '';
      if (input.kind === 'url') {
        host = input.value;
        try {
          host = new URL(input.value.startsWith('http') ? input.value : `https://${input.value}`).host || input.value;
        } catch {
          /* not a parseable URL — show what they typed */
        }
      } else if (input.kind === 'doc') {
        host = input.file.name;
      } else {
        host = 'what you told us';
      }

      pushTurn({
        role: 'user',
        text: input.kind === 'doc' ? input.file.name : input.value,
        attachment: input.kind === 'doc' ? 'doc' : input.kind === 'url' ? 'url' : null,
      });
      pushTurn({ role: 'url_reading', host, kind: input.kind === 'doc' ? 'doc' : 'url' });
      setPending(true);
      // Put the step back, carrying the reason and what they typed. A wrong
      // address used to advance anyway: the read failed, we said so in a passing
      // system line, and the company walked into the cards with a dead website
      // on file. Their own skip still works; ours does not stand in for it.
      const holdHere = (message: string) => {
        forcePaneToLastRef.current = true;
        setTurns(prev => [
          ...prev.filter(t => t.role !== 'url_reading' && t.role !== 'website_intake'),
          {
            role: 'website_intake',
            error: message,
            lastUrl: input.kind === 'url' ? input.value : undefined,
          },
        ]);
      };
      try {
        let resp;
        if (input.kind === 'doc') {
          const lower = input.file.name.toLowerCase();
          const isCsv = lower.endsWith('.csv') || input.file.type === 'text/csv';
          resp = await ingestFile(isCsv ? 'csv' : 'doc', input.file, input.file.name);
        } else {
          resp = await ingest(input.kind === 'url' ? 'url' : 'paste', input.value, clientId ?? undefined);
        }
        // `read_ok === false` is the backend saying it never got in. Anything
        // else, including an older backend that does not send the field, counts
        // as read.
        if (resp?.read_ok === false) {
          holdHere(
            resp.message
            || "We couldn't read that. Check the address, or give us a document or a description instead.",
          );
          return;
        }
        await refreshState();
      } catch (e) {
        console.error('[orbit] landing read failed', e);
        holdHere(
          "We couldn't read that. Check the address, or give us a document or a description instead.",
        );
        return;
      } finally {
        setPending(false);
      }
      advanceToChooser();
    },
    [clientId, refreshState, setTurns, advanceToChooser],
  );

  const sendUrl = useCallback(async (url: string) => {
    releaseWelcomeHold();
    pushTurn({ role: 'user', text: url, attachment: 'url' });
    // Professional staged status — push a url_reading turn so the pane
    // shows "Reading {host} → understanding what's relevant" instead of
    // a bare spinner while Firecrawl works. Removed on result.
    let readingHost = url;
    try { readingHost = new URL(url.startsWith('http') ? url : `https://${url}`).host || url; } catch {}
    pushTurn({ role: 'url_reading', host: readingHost });
    setPending(true);
    // Helper: strip the url_reading turn before pushing the result so
    // the pane lands on the actual outcome, not the status placeholder.
    const dropReading = () =>
      setTurns(prev => prev.filter(t => t.role !== 'url_reading'));
    try {
      const ingestResp = await ingest('url', url, clientId ?? undefined);
      // Never reached: say so instead of narrating what we "understood" from a
      // page we could not open.
      if (ingestResp?.read_ok === false) {
        dropReading();
        pushTurn({
          role: 'orbit',
          text: ingestResp.message
            || "Couldn't read that link. Check the address, or tell me about the business in your own words.",
        });
        return;
      }
      await refreshState();
      dropReading();
      // Summary-first: Orbit voices what it now understands about the
      // business, then asks the next question. The guessed fields are
      // reviewable in the dossier panel — no field-list card in chat.
      await afterIngest(ingestResp, `I just shared ${url}`);
    } catch (e) {
      console.error('[orbit] url ingest failed', e);
      dropReading();
      pushTurn({
        role: 'orbit',
        text: "Couldn't read that link. Try a different URL, or tell me about the business in your own words.",
      });
    } finally {
      setPending(false);
    }
  }, [clientId, refreshState, pushTurn, afterIngest]);

  // ── Doc upload ────────────────────────────────────────────────────
  const sendDoc = useCallback(async (file: File) => {
    releaseWelcomeHold();
    // Detect source by file shape so the backend picks the right pipeline.
    const lower = file.name.toLowerCase();
    const isCsv = lower.endsWith('.csv') || file.type === 'text/csv';
    const isImage = file.type.startsWith('image/');
    const source: 'csv' | 'image' | 'doc' = isCsv ? 'csv' : isImage ? 'image' : 'doc';

    pushTurn({ role: 'user', text: file.name, attachment: 'doc' });
    // Branded reading status — parity with the URL flow (docs used to show
    // only a bare spinner). Removed on result, replaced by the summary.
    pushTurn({ role: 'url_reading', host: file.name, kind: 'doc' });
    setPending(true);
    const dropReading = () =>
      setTurns(prev => prev.filter(t => t.role !== 'url_reading'));
    try {
      const ingestResp = await ingestFile(source, file, file.name);
      await refreshState();
      dropReading();
      // Same summary-first flow as URL: understanding summary → next question.
      await afterIngest(ingestResp, `I just uploaded ${file.name}`);
    } catch (e) {
      console.error('[orbit] doc upload failed', e);
      dropReading();
      pushTurn({
        role: 'orbit',
        text: "Couldn't read that file. Try a different one, or paste the key text in chat.",
      });
    } finally {
      setPending(false);
    }
  }, [clientId, refreshState, pushTurn, afterIngest]);

  // ── Drawer/right-rail commit ──────────────────────────────────────
  const handleCommitFromDrawer = useCallback(async (
    fieldKey: string, value: unknown, action: 'confirm' | 'correct',
  ) => {
    try {
      const c = await confirmApi(fieldKey, value, action, clientId ?? undefined);
      await refreshState();
      applyConverseResponse(c);
    } catch (e) {
      console.error('[orbit] drawer commit failed', e);
    }
  }, [clientId, refreshState, applyConverseResponse]);

  // Render a /confirm (chip) response into the chat. ONE place so every
  // chip path behaves identically and NONE can leave the screen blank:
  // 1) next_question speaks (prompt/directive) → render it; else
  // 2) the agent's orbit_reply → render it; else
  // 3) a safety-net line (locked → "all set", otherwise "tell me more").
  const renderConfirmResult = useCallback((c: ConfirmResponse) => {
    if (c.next_question?.prompt || c.next_question?.directive_type) {
      pushTurn({
        role: 'orbit',
        text: c.next_question.prompt || '',
        field: c.next_question.field,
        why: c.next_question.why,
        chips: chipsWithSkipMaybe(c.next_question),
        active: true,
        sectionTag: sectionTagFor(c.next_question.field),
        directiveType: c.next_question.directive_type ?? null,
        directivePayload: c.next_question.directive_payload ?? null,
      });
    } else if (c.orbit_reply) {
      pushTurn({ role: 'orbit', text: c.orbit_reply });
    } else {
      pushTurn({
        role: 'orbit',
        text: c.locked
          ? "You're all set on this one — I've got what I need. Want to add anything, or should I take it further?"
          : "I'm with you. Tell me a bit more and I'll keep going.",
      });
    }
  }, [pushTurn, chipsWithSkipMaybe, sectionTagFor]);

  // ── Multiselect submit ────────────────────────────────────────────
  // Fires when the user clicks "Done · N selected" on a multiselect
  // turn. Submits the FULL array via /confirm (not one at a time), so
  // partial state never leaks. After the commit, asks for the next
  // question via /converse with empty input.
  const handleMultiSubmit = useCallback(async (values: string[]) => {
    releaseWelcomeHold();
    const field = activeQuestion?.field;
    if (!field || !clientId) return;
    const labels = (activeQuestion?.chips ?? [])
      .filter((c) => values.includes(c.value))
      .map((c) => c.label);
    pushTurn({
      role: 'user',
      text: labels.length ? labels.join(', ') : `${values.length} selected`,
    });
    setPending(true);
    try {
      const c = await confirmApi(field, values, 'confirm', clientId);
      renderConfirmResult(c);
      await refreshState();
      applyConverseResponse(c);
    } catch (e) {
      console.error('[orbit] multi submit failed', e);
      pushTurn({ role: 'orbit', text: "Couldn't save those. Try again." });
    } finally {
      setPending(false);
    }
  }, [
    activeQuestion, clientId, refreshState, applyConverseResponse,
    renderConfirmResult, pushTurn,
  ]);

  // ── Date submit ───────────────────────────────────────────────────
  // A date question answers through the picker in the turn, not the
  // composer: the backend stores an ISO day and refuses anything else, so
  // "end of Q3" typed into the box would be silently dropped.
  const currentDateFor = useCallback((field?: string | null): string | null => {
    if (!field) return null;
    const [letter, subkey] = field.split('.');
    const section = ctx[`section_${letter}` as keyof CompanyContext] as
      Record<string, unknown> | undefined;
    const existing = section?.[subkey];
    return typeof existing === 'string' ? existing : null;
  }, [ctx]);

  const handleDateSubmit = useCallback(async (iso: string) => {
    releaseWelcomeHold();
    const field = activeQuestion?.field;
    if (!field || !clientId) return;
    pushTurn({ role: 'user', text: fmtDate(iso) });
    setPending(true);
    try {
      const c = await confirmApi(field, iso, 'confirm', clientId);
      renderConfirmResult(c);
      await refreshState();
      applyConverseResponse(c);
    } catch (e) {
      console.error('[orbit] date submit failed', e);
      pushTurn({ role: 'orbit', text: "Couldn't save that date. Try again." });
    } finally {
      setPending(false);
    }
  }, [
    activeQuestion, clientId, refreshState, applyConverseResponse,
    renderConfirmResult, pushTurn,
  ]);

  // ── Chip selection ────────────────────────────────────────────────
  // Action chips (e.g., __skip_this) trigger special flows. Regular
  // chips are structured answers — we apply them via /confirm directly
  // (deterministic save, no Sonnet extraction risk), then call
  // /converse with empty input for the next question.
  //
  // The legacy __autoscan_yes / __autoscan_no chips were retired when
  // email-domain inference moved to the BatchConfirmCard surface
  // (Slice 4 v2). The card now pre-fills `a.website` AND triggers the
  // Firecrawl scan when the user confirms — no separate yes/no chip
  // step needed.
  const handleChipSelect = useCallback(async (value: string, label: string) => {
    releaseWelcomeHold();
    // Skip current question (offered after 2+ unresolved asks)
    if (value === '__skip_this' && activeQuestion?.field) {
      pushTurn({ role: 'user', text: 'Skip this one' });
      setPending(true);
      try {
        const c = await confirmApi(activeQuestion.field, null, 'skip', clientId ?? undefined);
        renderConfirmResult(c);
        await refreshState();
        applyConverseResponse(c);
      } catch (e) {
        console.error('[orbit] skip failed', e);
      } finally {
        setPending(false);
      }
      return;
    }

    const field = activeQuestion?.field;
    if (!field) {
      // Defensive fallback: no question on screen, fall through to free-text.
      sendText(value);
      return;
    }

    // Render the human-readable label as the user turn (not the raw enum value).
    pushTurn({ role: 'user', text: label });
    setPending(true);
    try {
      const fieldDef = fields.find(f => f.key === field);
      let valueToCommit: unknown = value;
      if (fieldDef?.kind === 'multiselect') {
        const sectionKey = `section_${field.split('.')[0]}` as keyof CompanyContext;
        const subKey = field.split('.')[1];
        const section = ctx[sectionKey] as Record<string, unknown> | undefined;
        const existing = section?.[subKey];
        const existingList = Array.isArray(existing)
          ? existing.filter((v): v is string => typeof v === 'string')
          : typeof existing === 'string'
            ? [existing]
            : [];
        const withoutNone = existingList.filter(v => v !== 'none');
        valueToCommit = value === 'none'
          ? ['none']
          : Array.from(new Set([...withoutNone, value]));
      }

      const c = await confirmApi(field, valueToCommit, 'confirm', clientId ?? undefined);
      renderConfirmResult(c);
      await refreshState();
      applyConverseResponse(c);
    } catch (e) {
      console.error('[orbit] chip commit failed', e);
      pushTurn({ role: 'orbit', text: 'Couldn\'t save that. Try again.' });
    } finally {
      setPending(false);
    }
  }, [corporateDomain, activeQuestion, clientId, fields, ctx, sendUrl, sendText, refreshState, applyConverseResponse, pushTurn, renderConfirmResult]);


  // ── Approach pick → persist + route via route_plan ────────────────
  // Sends the picked approach to /submit so it's stored on the engagement
  // row, then navigates via route_plan.next_best_action.route (the single
  // source of truth for "where does this client go next"). Falls back to
  // legacy redirect_hint if the plan isn't returned for any reason.
  const handleApproachPick = useCallback(async (approach: Approach) => {
    setPicking(approach);
    try {
      const r = routePlan?.engagement_id && routePlan?.selected_approach
        ? await updateApproach(approach)
        : await submitApi(approach);
      const target = r.route_plan?.next_best_action?.route ?? r.redirect_hint;
      if (target) {
        navigate(target);
      }
    } catch (e) {
      console.error('[orbit] submit failed', e);
      setPicking(null);
    }
  }, [navigate, routePlan]);

  // ── v0.5 — directive callbacks ────────────────────────────────────
  // When the user clicks Draft the PRD on the propose_prd_draft card,
  // bypass the agent entirely and hit the deterministic /draft-prd-now
  // endpoint. The agent was politely acknowledging the click ("OK
  // drafting!") without actually calling the draft_prd tool — zero
  // engagement rows, zero validx projects. This path guarantees the
  // commit step fires.
  //
  // After kickoff, poll /home every 3s until engagement.validx_project_id
  // lands, then flip prdDrafting off and surface "Open workspace →".
  const handleDraftPrdAccept = useCallback(async () => {
    if (prdDrafting) return;

    // Find the most recent propose_prd_draft turn — its payload carries
    // the title / summary / integrations / success_metric the user just
    // saw and confirmed by clicking Draft. We hand those exact values
    // to the planner so the workspace PRD matches what was shown.
    const proposalTurn = [...turns].reverse().find(
      (t): t is Extract<Turn, { role: 'orbit' }> =>
        t.role === 'orbit' && t.directiveType === 'propose_prd_draft',
    );
    const payload = proposalTurn?.directivePayload ?? null;
    const title = (payload?.title || 'Your build').toString();

    setPrdDrafting(true);

    // Slice 12 — replace the static "Drafting your PRD" system line +
    // blind 60s poll with a live PrdDraftProgressCard. The card polls
    // /home itself, reads engagement.routing_decision.prd_draft_status,
    // transitions stages typographically, and surfaces the workspace
    // CTA when the validx project lands.
    pushTurn({
      role: 'prd_progress',
      payload: {
        title,
        startedAt: new Date().toISOString(),
      },
    });

    try {
      await draftPrdNow({
        clientId: clientId ?? undefined,
        title,
        summary: payload?.summary ?? undefined,
        integrations: payload?.integrations ?? undefined,
        successMetric: payload?.success_metric ?? undefined,
      });
      // The progress card handles polling + the "PRD ready" terminal
      // transition. The handler below (handlePrdProgressReady) routes
      // to the validx workspace when the card fires its onReady.
    } catch (e) {
      console.error('[orbit] draft-prd-now failed', e);
      pushTurn({ role: 'system', kind: 'warn', text: "Couldn't kick off the draft — try again." });
      setPrdDrafting(false);
    }
  }, [prdDrafting, clientId, turns, pushTurn]);

  // Slice 12 v2 — small helper that opens the validx workspace via the
  // HMAC bridge. The in-app /agents/[id] viewer was retired (the workspace
  // lives on validx), so a bridge failure surfaces a soft error.
  const openWorkspace = useCallback(async (workspaceProjectId: string) => {
    let workspaceUrl = '';
    try {
      const cid = clientId ?? localStorage.getItem('client_id') ?? '';
      // Land on the PLAN view — that's the validx workspace's
      // non-technical, presentable PRD (the "proposal" the user just
      // approved). Tasks board is the wrong first impression; the user
      // wants to SEE their PRD proposal on arrival.
      const redirectPath =
        `/customer/agent/${workspaceProjectId}` +
        `?clientId=${cid}` +
        `&view=plan`;
      const data = await requestAgentBridgeUrl(redirectPath, cid || undefined);
      if (data?.url) workspaceUrl = data.url as string;
    } catch (err) {
      console.warn('[orbit] bridge url unavailable', err);
    }
    if (workspaceUrl) {
      window.open(workspaceUrl, '_blank', 'noopener,noreferrer');
    } else {
      pushTurn({ role: 'system', kind: 'warn', text: "Couldn't open the workspace — please try again." });
    }
  }, [clientId, pushTurn]);

  // Slice 12 v2 — fired by PrdDraftProgressCard's onDone, which only
  // triggers once prd_draft_status.done === true. Because the
  // PRD_DRAFT_READY webhook writes prd_document AND done=true atomically,
  // the document is GUARANTEED present here — so this never flashes an
  // empty review card (the old bug: it fired on validx_project_id, which
  // lands on-demand long before the PRD content exists). Clears the
  // mid-flight flag and pushes the inline review card (sections + the
  // workspace / dashboard actions). De-dupes so re-polls don't stack
  // multiple review cards.
  const prdReviewPushedRef = useRef(false);
  const handlePrdDraftDone = useCallback(async () => {
    setPrdDrafting(false);
    if (prdReviewPushedRef.current) return;
    if (!clientId) return;
    try {
      const home = await fetchHome(clientId);
      const rd = (home.engagement?.routing_decision ?? {}) as Record<string, unknown>;
      const engDoc = rd.prd_document as
        | (PrdDocument & { title?: string })
        | undefined;
      // SCOPE THE PRD TO THE ACTIVE PROBLEM. Prefer the problem's OWN
      // prd_document (stored per-problem on its card, surfaced in home.builds).
      // Only fall back to the engagement-level prd_document when it actually
      // belongs to THIS problem (validx match — covers older PRDs stored
      // engagement-level before per-problem storage) or for generic intake
      // with no specific problem. This both (a) shows the leads problem its
      // OWN PRD and (b) never shows it another problem's (the brndsh-PRD-on-
      // leads bug). Read-only over home.builds — no existing build is touched.
      const _activePid = activeProblemIdRef.current;
      const _myBuild = _activePid
        ? (home.builds ?? []).find(b => b.problem_id === _activePid)
        : undefined;
      let doc: (PrdDocument & { title?: string }) | undefined =
        (_myBuild?.prd_document as (PrdDocument & { title?: string }) | null | undefined) ?? undefined;
      if (!doc) {
        const _engMatchesThisProblem =
          !!engDoc?.validx_project_id && engDoc.validx_project_id === _myBuild?.project_id;
        if (!_activePid || _engMatchesThisProblem) doc = engDoc;
      }
      const pid =
        doc?.validx_project_id
        ?? _myBuild?.project_id
        ?? home.engagement?.validx_project_id
        ?? home.builds?.[0]?.project_id
        ?? null;
      if (_activePid && !doc) {
        // This problem hasn't drafted its OWN PRD — don't surface another
        // problem's. Leave it on its scoping/draft state.
        return;
      }
      if (doc && doc.sections && Object.keys(doc.sections).length > 0) {
        prdReviewPushedRef.current = true;
        pushTurn({
          role: 'prd_review',
          payload: {
            title: doc.title || 'Your PRD',
            document: {
              title: doc.title || 'PRD',
              markdown: doc.markdown || '',
              sections: doc.sections,
              validx_project_id: pid ?? undefined,
              drafted_at: doc.drafted_at || new Date().toISOString(),
            },
          },
        });
        // The PRD exists now, so the person goes where it lives. The review
        // card stays in the thread behind them.
        exitOrbit();
        return;
      }
      // No structured document (older drafts) — fall back to the workspace.
      if (pid) void openWorkspace(pid);
    } catch (err) {
      console.warn('[orbit] failed to load prd_document for review', err);
    }
  }, [clientId, openWorkspace, pushTurn, exitOrbit]);

  // Coaching mode — user tapped a candidate chip on FieldSuggestionsCard.
  // Mark the turn picked (locks the chips so they can't be re-tapped)
  // and fire sendText(value) so the candidate flows through the normal
  // /converse pipeline as the user's answer to the active field.
  const handleFieldSuggestionPick = useCallback(
    (turnIndex: number, value: string) => {
      setTurns((prev) => prev.map((t, i) =>
        i === turnIndex && t.role === 'field_suggestions'
          ? { ...t, picked: true }
          : t,
      ));
      void sendText(value);
    },
    [sendText],
  );

  // "Write my own" — focus the composer instead of picking a candidate.
  // No state change needed; the composer is already visible below the
  // pane. We just leave the card in its picked=false state so the user
  // can still pick a chip if they change their mind while typing.
  const handleFieldSuggestionWriteOwn = useCallback(() => {
    // The composer's input takes focus naturally on the next user
    // interaction. Nothing else needed here — the chip card stays
    // visible above the composer until the user submits a reply.
  }, []);

  // On-demand coaching (problem flow only). The free-type problem fields with
  // no chips (success criteria, the workflows, the trigger, problem statement)
  // don't auto-generate options anymore — Orbit shows a compact "Show me a few
  // ways to answer" button under the question. The user taps it → loading →
  // Orbit generates 2-3 grounded option blocks inline. Explicit "you decide"
  // still auto-fills via the server backstop; this is the proactive affordance.
  const COACHABLE_FIELDS = useMemo(() => new Set([
    'e.problem_summary', 'e.success_criteria', 'e.acceptance_criteria',
    'e.current_workflow', 'e.desired_workflow', 'e.trigger_event',
  ]), []);
  const [coachLoadingField, setCoachLoadingField] = useState<string | null>(null);
  const requestFieldCoach = useCallback(async (field: string) => {
    if (!field || coachLoadingField) return;
    setCoachLoadingField(field);
    try {
      const r = await fetchFieldCandidates(field, clientId ?? undefined);
      if (Array.isArray(r.candidates) && r.candidates.length >= 2) {
        const payload = { field: r.field, label: r.label ?? field, intro: r.intro ?? '', candidates: r.candidates };
        setTurns(prev => {
          let idx = -1;
          for (let i = prev.length - 1; i >= 0; i -= 1) {
            const tt = prev[i];
            if (tt.role === 'orbit' && (tt.field === field || (!tt.field && i === prev.length - 1))) { idx = i; break; }
          }
          if (idx < 0) return prev;
          const next = [...prev];
          next[idx] = { ...(next[idx] as Extract<Turn, { role: 'orbit' }>), fieldSuggestions: payload };
          return next;
        });
      }
    } catch { /* silent — the input still works as a fallback */ } finally {
      setCoachLoadingField(null);
    }
  }, [clientId, coachLoadingField]);

  const handleDraftPrdEdit = useCallback(() => {
    // User wants to edit something before drafting. Send a synthetic
    // input so the agent re-engages on what to refine.
    pushTurn({ role: 'orbit', text: "What would you like to refine before I draft it?" });
  }, [pushTurn]);

  const handleDraftPrdAddMore = useCallback(() => {
    pushTurn({ role: 'orbit', text: "Sure — what else should I know?" });
  }, [pushTurn]);

  // Paid-human card — open cal.com slot picker in new tab. Don't
  // dismiss the conversation, just open the booking surface.
  const handleOpenCalendar = useCallback((url: string) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  // User picked a mock slot — persist via /api/intake/paid-human/book,
  // push a confirmation chat turn so the booking survives reloads via
  // the orbit:turns localStorage cache. When cal.com integration lands,
  // the backend endpoint will also call cal.com's /bookings API.
  const handleBookPaidHumanSlot = useCallback(async (slot: {
    slotId: string;
    dateLabel: string;
    timeLabel: string;
    consultantName?: string;
    consultantRole?: string;
  }) => {
    const { bookPaidHumanSlot } = await import('./lib/intakeApi');
    const result = await bookPaidHumanSlot({
      clientId: clientId ?? undefined,
      slotId: slot.slotId,
      dateLabel: slot.dateLabel,
      timeLabel: slot.timeLabel,
      consultantName: slot.consultantName,
      consultantRole: slot.consultantRole,
    });
    const who = slot.consultantName || 'a Solution partner';
    pushTurn({
      role: 'orbit',
      text: (
        `Session booked — ${result.date_label} · ${result.time_label} with ${who}. ` +
        `Confirmation lands in your inbox. They'll read your scope before the call, ` +
        `so you don't have to repeat anything.`
      ),
    });
  }, [clientId, pushTurn]);

  // User clicked the soft "Recommended: Solution partner" pill —
  // expand the full PaidHumanCard inline by synthesizing an orbit turn
  // that carries the offer_paid_human directive. The reason copy comes
  // from the Suggestion Generator's rule-specific suggestion_line so
  // the user sees WHY paid-human came up (regulated industry, burned
  // investment, asked for done-for-me).
  const handleOpenPaidHumanFromPill = useCallback(() => {
    if (!suggestion || suggestion.approach !== 'paid_human') return;
    if (paidHumanAlreadyBooked) return;
    setPaidHumanPillOpened(true);
    pushTurn({
      role: 'orbit',
      text: suggestion.suggestion_line || 'A Solution partner can scope this with you.',
      directiveType: 'offer_paid_human',
      directivePayload: {
        reason: suggestion.suggestion_line || "If it would help, you don't have to scope this alone.",
        consultant_name: 'A Solution partner from our team',
        consultant_role: 'Practice lead',
        slot_picker_url: 'https://cal.com/team-brndsh/scoping',
        what_to_expect: [
          'Reads your scope before the call.',
          "30-minute session — you don't repeat anything.",
          'You walk out with a PRD and a workspace ready to build.',
        ],
      },
    });
  }, [suggestion, paidHumanAlreadyBooked, pushTurn]);

  // Orbit redesign 6a — the landing chooser pick. Replace the chooser with a
  // path-framed opener (Solve a problem / Map the business) and let the user
  // type — the backend agent + gate take over from their first message.
  // Explore leaves Orbit for the marketplace catalog.
  const handleRoutePick = useCallback((route: OrbitRoute) => {
    // If we're already on this route AND it's not a cold start, don't clobber.
    // The drawer asserts the route on every open, so without this, reopening
    // the drawer wipes the ongoing chat and resets to the first question.
    const isCompanyConvo = turns.some(t => t.role === 'orbit' && typeof t.field === 'string' && /^[abcd]\./.test(t.field));
    if (route === 'company' && isCompanyConvo) {
        // We are ALREADY in the company mapping workflow. Do not restart.
        return;
    }
    releaseWelcomeHold();
    // Declare the picked route eagerly so the very next turn already carries it
    // (the backend re-asserts what the turn declares) — no waiting on the
    // fire-and-forget selectRoute below to land first.
    routeRef.current = route;
    // Persist the picked path so the backend Inquirer scopes the FIRST
    // question to it (problem → Section E). Fire-and-forget: it lands well
    // before the user finishes typing their opener answer, and the converse
    // that follows reads the persisted route. Non-fatal if it races/fails —
    // the converse-path scoping still kicks in once problem text exists.
    selectRoute(route, clientId ?? undefined).catch(() => { /* best effort */ });
    // Optimistically reflect the route in ctx NOW so the dossier scopes to
    // the right field set immediately — on the problem route that means the
    // 23 Section-E problem fields show as the shape from the very first
    // screen, without waiting for the next /state refresh.
    if (route === 'problem' || route === 'company') {
      setCtx(prev => ({
        ...prev,
        field_states: {
          ...(prev.field_states || {}),
          _selected_route: { state: 'system', value: route },
        } as unknown as CompanyContext['field_states'],
      }));
    }
    if (route === 'explore') {
      navigate('/marketplace');
      return;
    }
    setTurns(prev => prev.filter(t => t.role !== 'route_chooser'));
    if (route === 'problem' && pluginScopingEnabled) {
      // The builder's plugin owns the scoping conversation from here.
      setPluginScoping(true);
      return;
    }
    if (route === 'problem') {
      pushTurn({
        role: 'orbit',
        text: "Tell me the problem you want to solve — what's draining the team, or what's broken today?",
        why: "Describe it in your own words. I'll structure it and only ask what I genuinely need.",
        active: true,
      });
    } else {
      // Company route = the WALK. It derives its own position from the
      // dossier, so it needs no opener and no conversation turn: all this has
      // to do is assert the route and pull fresh state for the panes around
      // it. The old opener ran a converse('') here, which (a) asked the
      // website question the walk was already asking and (b) let the intake
      // agent re-extract stale facts as user_confirmed, clobbering answers the
      // person had just corrected. Persist the route, refresh, stand down.
      void (async () => {
        try {
          await selectRoute(route, clientId ?? undefined).catch(() => { /* best effort */ });
          await refreshState();
        } catch (e) {
          console.error('[orbit] company route assert failed', e);
        }
      })();
      return;
    }
  }, [navigate, pushTurn, releaseWelcomeHold, clientId, ctx.section_a, ctx.field_states, refreshState, applyConverseResponse, pushNextQ]);

  // ── External route assertion ──────────────────────────────────────
  // The conversation, its turns, and the backend route are ONE per client,
  // shared by both mounts. So when a host surface opens Orbit with a clear
  // intent — "Scope a problem" (Problems page, global header) or the company
  // map (My Business) — it dispatches `orbit:route` so the ACTIVE question
  // matches that intent instead of resuming wherever the other surface left
  // off (the section-G company question the user hit when they meant to
  // scope a problem). We skip if Orbit is already sitting on a fresh,
  // unanswered opener for that route, so repeated opens don't stack openers.
  useEffect(() => {
    const onRoute = (e: Event) => {
      // A per-problem mount (context 'problem:<id>') is bound to ONE problem and
      // owns its own resume flow — it must NEVER react to the generic route
      // assertion, or handleRoutePick('problem') below pushes the fresh "Tell me
      // the problem you want to solve" opener OVER the resume. (The drawer fires
      // orbit:route on every open; this is the per-problem guard.)
      if (_ctxProblemId) return;
      const route = (e as CustomEvent).detail?.route as OrbitRoute | undefined;
      if (!route || route === 'explore') return;
      const last = turns[turns.length - 1];
      if (route === 'problem') {
        const onFreshProblem = !!last && last.role === 'orbit' && !!last.active
          && typeof last.text === 'string' && last.text.startsWith('Tell me the problem');
        if (onFreshProblem) return;
      } else if (route === 'company') {
        // "Map your business" is client-level — drop any active problem binding.
        setActiveProblemId(null);
        // (No setContext here — `context` is a prop with no setter, and this
        // path is only reached when _ctxProblemId is falsy anyway since line
        // ~2578 returns early otherwise. The clean remount is the setTurns([])
        // + handleRoutePick('company') reset below.)

        // Is the chat currently showing a PROBLEM conversation? The drawer
        // shares ONE turn-stream across both jobs, so opening on the company
        // route while a problem was on screen left the stale problem showing —
        // the "I clicked Map your business but it shows my last problem" bug.
        // When that's the case, RESET to a clean company map. The problem stays
        // safe on its card (resume it from /problems) — we're only clearing the
        // drawer's view, not the data.
        const onProblemConvo =
          !!activeProblemIdRef.current
          || turns.some(t =>
            (t.role === 'orbit' && typeof t.field === 'string' && t.field.startsWith('e.'))
            || (t.role === 'orbit' && typeof t.text === 'string' && t.text.startsWith('Tell me the problem'))
            || t.role === 'problem_canvas' || t.role === 'prd_review' || t.role === 'prd_progress');
        if (onProblemConvo) {
          setTurns([]);                // clear the stale problem conversation
          handleRoutePick('company');  // seed/resume the company map on a clean slate
          return;
        }

        // Already on company → preserve a genuine mid-company session instead
        // of re-seeding a stale opener. Bail only if the user has replied, or
        // we've resumed past the website onto a later A→D question.
        const hasUserTurn = turns.some(t => t.role === 'user');
        const onLaterCompanyQ = !!last && last.role === 'orbit' && !!last.active
          && typeof last.field === 'string'
          && /^[abcd]\./.test(last.field) && last.field !== 'a.website';
        if (hasUserTurn || onLaterCompanyQ) {
            // Already mid-company convo, do nothing so the chat naturally resumes.
            return;
        }
      }
      handleRoutePick(route);
    };
    window.addEventListener('orbit:route', onRoute);
    return () => window.removeEventListener('orbit:route', onRoute);
  }, [handleRoutePick, turns, routePlan]);

  // Slice 5/7 — EnterL2 fork. "Continue with me" triggers Phase 2
  // entry: backend lazy-creates engagement_request + seeds problem_card
  // from L1.E. Frontend pushes a ProblemCanvas turn so the user sees
  // the scoped problem pinned in the chat. Inquirer continues to
  // handle Phase 1 (H/I/M/N/O) ranking via /converse in parallel.
  const handleEnterL2Continue = useCallback(async () => {
    try {
      // "Continue DIY" → enter the L2 phase walk at Phase 1 in chat. No
      // paywall: the backend enters Phase 1 straight away, resuming from
      // everything already captured (the dossier is the state, so nothing the
      // user answered before this fork is lost or re-asked).
      const r = await enterCustomBuild(clientId ?? undefined, window.location.pathname);
      // Legacy: nothing charges for deep scoping any more, so the backend
      // returns Phase 1 directly. It can only ask for payment if a superuser
      // deliberately re-enables the retired gate (ORBIT_DEEP_SCOPING_PAYWALL=1),
      // in which case honour it rather than stalling on a null question.
      if (r.requires_payment && r.checkout_url) {
        try { window.sessionStorage.setItem('orbit_deep_scope_pending', '1'); } catch { /* ignore */ }
        window.location.assign(r.checkout_url);
        return;
      }
      // (Phase 2's ProblemCanvas surfaces later, when Phase 1 completes and
      // the phase machine emits the enter_problem_scoping directive.)
      pushTurn({
        role: 'orbit',
        text: "You're in. Let's go deeper — first the company picture, then the work itself, then the outcomes. I'll draft the PRD once we've got it all.",
      });
      pushNextQ(r.next_question ?? null);
      // Wire the question as active so its chips are interactive (see
      // handlePickCustomBuild — pushNextQ alone renders static chips).
      setQuestion(r.next_question ?? null);
      await refreshState();
    } catch (e) {
      console.error('[orbit] enter_l2 continue failed', e);
      pushTurn({
        role: 'system',
        kind: 'warn',
        text: "Couldn't start scoping just now. Try again in a moment.",
      });
    }
  }, [clientId, pushTurn, pushNextQ, refreshState]);

  // Deep-scoping return: LEGACY. The deep-scoping paywall is retired, so no
  // new checkout is ever minted from this app. This only catches a client who
  // was mid-checkout when the paywall came down (?deep_scoping=paid|cancelled
  // still in the URL): acknowledge it, drop the stale in-flight marker so a
  // refresh or Back never revives the pay-gate copy, and resume the walk from
  // whatever the dossier already holds. Runs once.
  useEffect(() => {
    if (deepScopingResumeRef.current) return;
    if (!bootstrapReady) return;
    const _params = new URLSearchParams(window.location.search);
    const ds = _params.get('deep_scoping');
    // The marker survives a browser-Back that returns without our params.
    let pending = false;
    try { pending = window.sessionStorage.getItem('orbit_deep_scope_pending') === '1'; } catch { /* ignore */ }
    // Always drop it, so it can never revive pay-gate copy on a later reload.
    try { window.sessionStorage.removeItem('orbit_deep_scope_pending'); } catch { /* ignore */ }
    if (!ds && !pending) return;
    deepScopingResumeRef.current = true;
    const dsSession = _params.get('ds_session');
    if (ds) window.history.replaceState({}, document.title, window.location.pathname);
    setPending(true);
    void (async () => {
      try {
        // Never conclude "no charge" from the URL alone: the success param
        // can be lost on the round-trip. Ask the backend, which falls back to
        // the checkout session it stashed at creation, and only say it landed
        // when it verifiably did. Either way the walk continues.
        const v = await confirmDeepScoping(dsSession ?? undefined, clientId ?? undefined);
        if (v?.paid && v?.verified) {
          pushTurn({ role: 'system', kind: 'info', text: 'Payment received.' });
        }
        const r = await converse('', null, clientId ?? undefined, null, undefined, {
          problemId: activeProblemIdRef.current || undefined,
          newProblem: false,
        });
        applyConverseResponse(r);
        if (r.next_question?.prompt || r.next_question?.directive_type) {
          pushNextQ(r.next_question);
        }
      } catch (e) {
        console.warn('[orbit] deep-scope return resume failed', e);
      } finally {
        setPending(false);
      }
    })();
  }, [bootstrapReady, pushTurn, clientId, applyConverseResponse, pushNextQ, setPending]);

  // Slice 7 — ProblemCanvas "Keep scoping with Orbit" closes the canvas and
  // ADVANCES: once the problem is scoped (a workflow with trigger/steps/tools
  // flipped it to SCOPED), a converse turn lets the phase machine move
  // Phase 2 → Phase 3 and surface the next question (or the budget gate / PRD
  // card downstream). Keeps the canvas in scroll history so the user can refer
  // back.
  const handleProblemCanvasContinue = useCallback(async (turnIndex: number) => {
    setTurns(prev => prev.map((t, i) => (
      i === turnIndex && t.role === 'problem_canvas'
        ? { ...t, closed: true }
        : t
    )));
    try {
      setPending(true);
      await refreshState();
      const r = await converse('', null, clientId ?? undefined, null, undefined, _converseOpts());
      _adoptProblem(r);
      applyConverseResponse(r);
      if (r.next_question?.prompt || r.next_question?.directive_type) {
        pushNextQ(r.next_question);
      }
    } catch (e) {
      console.error('[orbit] problem-canvas continue advance failed', e);
    } finally {
      setPending(false);
    }
  }, [clientId, refreshState, applyConverseResponse, pushNextQ]);

  const handleEnterL2TalkToPartner = useCallback((forkPayload?: DirectivePayload) => {
    // Option A of the deep-scope fork, same path as the soft pill: push an
    // offer_paid_human turn so the user lands on the real slot picker. The
    // partner and cal.com link come from the fork card's payload (the backend
    // reads the superuser-set solution_partner_cal_url), with the previous
    // hardcoded values as the fallback for older turns.
    pushTurn({
      role: 'orbit',
      text: "Got it — let's get a Solution Partner on this with you.",
      directiveType: 'offer_paid_human',
      directivePayload: {
        reason: "You've scoped the problem. A partner can take the deeper passes with you.",
        reason_label: 'You asked for a partner',
        consultant_name: forkPayload?.partner_name || 'A Solution partner from our team',
        consultant_role: forkPayload?.partner_role || 'Practice lead',
        slot_picker_url: forkPayload?.slot_picker_url || 'https://cal.com/team-brndsh/scoping',
        what_to_expect: forkPayload?.what_to_expect || [
          'Reads your scope before the call.',
          "30-minute session — you don't repeat anything.",
          'You walk out with a PRD and a workspace ready to build.',
        ],
      },
    });
  }, [pushTurn]);

  // "Back" from the Solution-Partner slot picker → return to the card the
  // user came from (the deep-scope fork). Remove only STANDALONE
  // offer_paid_human turns (the ones the pill / TalkToPartner paths push —
  // they carry no `field`), never a question turn that merely also carries
  // the directive. The auto-advance effect only snaps on growth, so re-point
  // the pane to the new latest navigable turn here.
  const handleBackFromPaidHuman = useCallback(() => {
    let cut = turns.length;
    while (cut > 0) {
      const t = turns[cut - 1] as { role: string; directiveType?: string | null; field?: string | null };
      if (t.role === 'orbit' && t.directiveType === 'offer_paid_human' && !t.field) {
        cut -= 1;
      } else {
        break;
      }
    }
    if (cut >= turns.length) return; // nothing standalone to peel back
    const next = turns.slice(0, cut);
    setTurns(next);
    let idx = next.length - 1;
    while (idx > 0 && (next[idx].role === 'user' || next[idx].role === 'system')) idx -= 1;
    setActiveTurnIdx(Math.max(0, idx));
  }, [turns]);

  // Slice 6 — Send-to-role magic-link delegation. Click the role chip
  // on a chat turn → prompt for recipient email → fire shareField → the
  // backend issues a scoped token + emails the recipient. Push a soft
  // system-line confirmation so the user knows it landed.
  const handleShareField = useCallback(
    async (fieldKey: string, role: string | null) => {
      const def = fields.find(f => f.key === fieldKey);
      const ROLE_LABELS: Record<string, string> = {
        ceo: 'CEO',
        cto: 'CTO',
        cfo: 'CFO',
        sponsor: 'Sponsor',
        admin: 'IT / Legal',
        measurement_owner: 'Measurement owner',
        dept_lead: 'Department lead',
      };
      const label = (role && ROLE_LABELS[role]) || 'a teammate';
      // Minimum-viable share UI: native prompt. Replace with an inline
      // input in a follow-up once the directive surface lands.
      const emailRaw = window.prompt(
        `Send "${def?.label ?? fieldKey}" to ${label}. What's their email?`,
        '',
      );
      const email = (emailRaw || '').trim();
      if (!email || !email.includes('@')) {
        return;
      }
      try {
        const res = await shareField({
          field: fieldKey,
          recipient_email: email,
          recipient_role: role ?? undefined,
          client_id: clientId ?? undefined,
          problem_id: activeProblemIdRef.current ?? undefined,
        });
        pushTurn({
          role: 'system',
          kind: 'success',
          text: `Sent to ${res.role_label || label} at ${email}. Link expires ${res.expires_at?.slice(0, 10) || 'in 14 days'}.`,
        });
      } catch (e) {
        console.error('[orbit] shareField failed', e);
        pushTurn({
          role: 'system',
          kind: 'warn',
          text: "Couldn't send that just now. Try again in a moment.",
        });
      }
    },
    [fields, clientId, pushTurn],
  );

  // Schedule an Orbit voice call to the right person to fill THIS block.
  // The question's field key carries its section (e.g. "i.tools" → section
  // "i"); the call is scoped to that section, so Orbit dials the person,
  // explains only this block, and fills it. Mirrors handleShareField's
  // minimum-viable prompt UI — the polished modal lives on the per-problem
  // detail page.
  const handleScheduleField = useCallback(
    async (fieldKey: string, role: string | null) => {
      const def = fields.find(f => f.key === fieldKey);
      const ROLE_LABELS: Record<string, string> = {
        ceo: 'CEO', cto: 'CTO', cfo: 'CFO', sponsor: 'Sponsor',
        admin: 'IT / Legal', measurement_owner: 'Measurement owner',
        dept_lead: 'Department lead',
      };
      const label = (role && ROLE_LABELS[role]) || 'a teammate';
      const section = (fieldKey.split('.')[0] || '').toLowerCase();
      const engagementId = routePlan?.engagement_id;
      if (!engagementId) {
        pushTurn({
          role: 'system',
          kind: 'info',
          text: "I can set up a call once we've started scoping this problem properly.",
        });
        return;
      }
      const name = (window.prompt(
        `Schedule an Orbit call with your ${label} to fill "${def?.label ?? section}". Their name?`,
        '',
      ) || '').trim();
      const phone = (window.prompt(
        `Phone number for Orbit to call ${name || label} on?`,
        '',
      ) || '').trim();
      if (!phone) return;
      try {
        const r = await scheduleSectionCall(engagementId, {
          section,
          to_number: phone,
          recipient_name: name || undefined,
          problem_id: activeProblemIdRef.current ?? undefined,
        });
        pushTurn({
          role: 'system',
          kind: 'success',
          text: `Orbit will call ${name || label} to fill this block — ${r.status === 'scheduled' ? 'scheduled' : 'dialing now'}.`,
        });
      } catch (e) {
        console.error('[orbit] scheduleSectionCall failed', e);
        pushTurn({
          role: 'system',
          kind: 'warn',
          text: "Couldn't set up that call just now. Try again in a moment.",
        });
      }
    },
    [fields, routePlan, pushTurn],
  );

  const handleDismissPaidHuman = useCallback(() => {
    // Soft dismiss — flag so the agent doesn't re-render it next turn
    // unless scoping difficulty escalates further.
    try {
      window.localStorage.setItem(
        `orbit:paid-human-dismissed:${clientId || 'anon'}`,
        new Date().toISOString(),
      );
    } catch (_) { /* storage full / private mode */ }
    pushTurn({ role: 'system', kind: 'info', text: "Got it — keep going with me." });
  }, [clientId, pushTurn]);

  // Marketplace match cards — picking one navigates to the
  // marketplace listing for that agent. Custom Build continues the
  // chat (agent's next turn proposes the PRD draft directly).
  const handlePickMarketplace = useCallback((slug: string) => {
    setMarketplacePicking('marketplace');
    navigate(`/marketplace/${slug}`);
  }, [navigate]);

  const handlePickCustomBuild = useCallback(async () => {
    if (marketplacePicking) return;
    setMarketplacePicking('custom');
    try {
      // Custom build → start the L2 phase walk at Phase 1 (Company Shell).
      // The phase machine then sequences Phase 1 → 2 → 3 and offers the PRD
      // only after Phase 3.
      const r = await enterCustomBuild(clientId ?? undefined, window.location.pathname);
      // Legacy: nothing charges for deep scoping any more, so the backend
      // returns Phase 1 directly. It can only ask for payment if a superuser
      // deliberately re-enables the retired gate (ORBIT_DEEP_SCOPING_PAYWALL=1),
      // in which case honour it rather than stalling on a null question.
      if (r.requires_payment && r.checkout_url) {
        try { window.sessionStorage.setItem('orbit_deep_scope_pending', '1'); } catch { /* ignore */ }
        window.location.assign(r.checkout_url);
        return;
      }
      pushTurn({
        role: 'orbit',
        text: "Custom build it is. I'll scope this properly with you — a few deeper passes on your company, the work itself, then the outcomes, and I'll draft the PRD at the end.",
      });
      pushNextQ(r.next_question ?? null);
      // pushNextQ only renders the turn's chips — the interactive selection +
      // multiselect "Done" hang off activeQuestion (= `question`). Without
      // this, the Phase-1 question's chips render but can't be picked.
      setQuestion(r.next_question ?? null);
      await refreshState();
    } catch (e) {
      console.error('[orbit] custom build pick failed', e);
      pushTurn({ role: 'system', kind: 'warn', text: "Couldn't start custom build just now. Try again." });
    } finally {
      setMarketplacePicking(null);
    }
  }, [marketplacePicking, clientId, pushTurn, pushNextQ, refreshState]);

  // ── §F education panel ────────────────────────────────────────────
  const shouldShowEducation = useMemo(() => {
    if (eduDismissed) return false;
    // activeQuestion can exist with a null field (e.g. a recovered or
    // directive-only question). Guard before calling startsWith — a null
    // .field was crashing the whole pane with "reading 'startsWith'".
    if (!activeQuestion || !activeQuestion.field) return false;
    return activeQuestion.field.startsWith('f.');
  }, [eduDismissed, activeQuestion]);

  // ── Render ────────────────────────────────────────────────────────
  // Hero stays visible until the USER speaks (not until Orbit's opener lands).
  const coldStart = !turns.some(t => t.role === 'user');

  // The landing chooser is a pure decision moment — three cards, no capture.
  // Hide the composer entirely while it's the active turn: there's nothing
  // to type until the user picks a path, and an input rail under the cards
  // reads as "or just talk to me," muddying a clean fork. The composer
  // returns the instant a path is chosen (handleRoutePick swaps in an
  // orbit turn).
  const chooserActive = turns[activeTurnIdx]?.role === 'route_chooser';
  // A1 — the landing read is self-contained: it carries its own three inputs
  // and its own Continue/Skip. It also has to be answered BEFORE Orbit opens
  // its mouth. Voice auto-starts as the default way in, so without this the
  // spoken greeting asks what problem you want to solve over the top of a
  // screen asking for your website, and answers the wrong question first.
  const landingReadActive = turns[activeTurnIdx]?.role === 'website_intake';
  // A website on file that nobody has confirmed yet (a Solution Partner typed
  // it, or a read inferred it). The landing question opens with it in the
  // field so the owner confirms instead of retyping, even when the turn was
  // restored from storage without the address on it.
  const websiteGuess = (() => {
    const state = (ctx.field_states || {})['a.website']?.state;
    if (state === 'user_confirmed' || state === 'user_corrected' || state === 'skipped') return '';
    const value = ctx.section_a?.website;
    if (typeof value !== 'string') return '';
    return value.trim();
  })();

  // Topbar dossier pill count — scoped to the chosen route so it matches the
  // DossierPanel ("X / 23" on the problem route), NOT the full 60-field
  // universe. Mirrors the panel's scoping; falls back to the backend's full
  // count only when no route is selected yet.
  const scopedCounts = useMemo(() => {
    const fs = ctx.field_states ?? {};
    const routeVal = ((fs['_selected_route'] as { value?: string } | undefined)?.value
      ?? (fs['_path'] as { value?: string } | undefined)?.value) as
      'problem' | 'company' | 'explore' | undefined;
    const isCap = (k: string) => ['user_confirmed', 'user_corrected', 'ai_guessed']
      .includes((fs[k] as { state?: string } | undefined)?.state ?? '');
    // Company MAPPING is the business picture: sections A·B·C·D. Budget (F)
    // and Engagement (G) are the commercial / decision layer (per the routing
    // model) — they qualify a specific DEAL, not the company, so they're NOT
    // part of "map the business" and are excluded from this dossier count.
    // Keeps this counter consistent with the My Business company-profile count.
    const COMPANY = ['a', 'b', 'c', 'd'];
    let scoped: FieldDef[];
    if (routeVal === 'problem') {
      scoped = fields.filter(f => f.section === 'e' && f.key !== 'e.flag_reason');
    } else if (routeVal === 'company') {
      scoped = fields.filter(f => COMPANY.includes(f.section) && f.key !== 'a.industry_detail');
    } else {
      return counts; // no route yet → keep the backend's full count
    }
    const captured = scoped.reduce((n, f) => n + (isCap(f.key) ? 1 : 0), 0);
    return { captured, total: scoped.length };
  }, [ctx.field_states, fields, counts]);

  // The walk OWNS the company route. Mapping a business is a flow, not a
  // chat: there is no prose door out of it, and therefore no composer under
  // it — the two ways through are Orbit speaking the script or the person
  // typing the answers, both inside the walk.
  const walkActive = isCompanyRoute && !chooserActive && !landingReadActive;

  // A3 — mapping the business is a set of cards, not a question queue. The
  // dossier already held every answer; what was missing was showing it back a
  // section at a time, filled in, in plain language. The walk stays reachable
  // because it is the only surface with voice mode, and some people would
  // rather be asked than read a form.
  const [companyMode, setCompanyMode] = useState<'cards' | 'walk'>('cards');
  // Where to reopen the cards when someone is sent back to finish something.
  const [cardStartLetter, setCardStartLetter] = useState<string | undefined>(undefined);

  // Answers the company still owes: visible L1 fields, not optional, no value.
  // Optional ones are deliberately not gaps — blank is a complete answer there.
  const requiredGaps = useMemo(
    () => fields
      .filter(f => (f.layer || 'L1') === 'L1' && !f.hidden && !f.optional)
      .filter((f) => {
        const letter = f.key.split('.')[0];
        const section = (ctx as unknown as Record<string, Record<string, unknown> | undefined>)[
          `section_${letter}`
        ] ?? {};
        return !isFilledValue(section[subKey(f.key)]);
      })
      .map(f => f.key),
    [fields, ctx],
  );

  // The end of the cards.
  //
  // Complete, and the profile is worth opening. Not complete, and the profile
  // would read half-empty, so this lands on the mapping ON the profile instead,
  // which is where the progress and "continue mapping" live.
  //
  // The WALK decides, not a count of our own. It already knows which questions
  // this company is asked, it already excludes the optional ones, and it is what
  // the dossier pill in the header reports. A second opinion computed here is
  // how you end up with a pill reading 23 of 29 beside a screen saying finished.
  // Someone coming back to finish opens on the first card that is missing
  // something, not on card one. Once only: recomputing this as they answer would
  // yank them between cards while they are working.
  const pickedStartCard = useRef(false);
  useEffect(() => {
    if (pickedStartCard.current) return;
    if (fields.length === 0 || requiredGaps.length === 0) return;
    pickedStartCard.current = true;
    setCardStartLetter(requiredGaps[0].split('.')[0]);
  }, [fields.length, requiredGaps]);

  const finishMapping = useCallback(async () => {
    let done = true;
    try {
      const walk = await getBusinessWalk(clientId ?? undefined);
      done = Boolean(walk?.done);
    } catch (e) {
      // A failed check must never trap anyone at the end of their onboarding.
      console.error('[orbit] walk check failed', e);
    }
    goAndClose(done ? '/my-business' : '/my-business?map=1');
  }, [clientId, goAndClose]);

  // A card row is not a conversation, so it does not wait for one. `ask_next`
  // false drops the two model calls the chat needs after a write, and the reply
  // carries the saved sections, so the row goes green in one fast request
  // instead of spinning for the nine to twenty-one seconds measured before.
  // The full state still refreshes, just not on the person's critical path.
  const commitCardField = useCallback(
    async (key: string, value: unknown, action: 'confirm' | 'correct') => {
      const res = await confirmApi(key, value, action, clientId ?? undefined, 'L1', false);
      if (res?.company_context) {
        setCtx(prev => ({
          ...prev,
          ...(res.company_context as Partial<typeof prev>),
          field_states: res.field_states ?? prev.field_states,
          completion_pct: res.completion_pct ?? prev.completion_pct,
        }));
      }
      // A conditional question appears (or stops applying) the moment the answer
      // that gates it lands, on the card being looked at, rather than turning up
      // on a later visit because the visibility flags were read once at load.
      if (res?.hidden_keys) {
        const nowHidden = new Set(res.hidden_keys);
        setFields(prev => prev.map(f => (
          f.hidden === nowHidden.has(f.key) ? f : { ...f, hidden: nowHidden.has(f.key) }
        )));
      }
      refreshState().catch(() => {});
    },
    [clientId, refreshState],
  );

  // A4 — the stack card shows which of the company's tools are actually
  // connected, so it needs the connection list. Loaded lazily (only the
  // company route renders the card) and re-read after every connect.
  const [connections, setConnections] = useState<OnboardingConnectionRow[]>([]);
  const reloadConnections = useCallback(async () => {
    try {
      setConnections(await fetchConnections(clientId ?? undefined));
    } catch (e) {
      // A missing connection list must not blank the card — it just means every
      // tool shows as not-yet-connected, which is the safe reading.
      console.error('[orbit] connections load failed', e);
    }
  }, [clientId]);

  // Load the connection list when the cards become reachable, not on mount:
  // the problem and explore routes never render the stack card.
  useEffect(() => {
    if (!isCompanyRoute) return;
    void reloadConnections();
  }, [isCompanyRoute, reloadConnections]);

  // A6 — a field the company just added only exists in the registry, so the
  // card needs a fresh schema (not fresh state) before it can draw the row.
  // Section values are `unknown`; the org chart field is a url string when set.
  const asText = (v: unknown): string | null =>
    typeof v === 'string' && v.trim() ? v.trim() : null;

  // Registry labels for the known tool values, so 'hubspot' reads as 'HubSpot'
  // from the same source the multiselect uses rather than a second table.
  const toolOptionLabels = useMemo(() => {
    const f = fields.find(x => x.key === 'b.tools_used');
    return Object.fromEntries((f?.options ?? []).map(o => [o.value, o.label]));
  }, [fields]);

  const saveTools = useCallback(
    async (next: string[]) => {
      const had = Array.isArray(ctx.section_b?.tools_used)
        && (ctx.section_b.tools_used as unknown[]).length > 0;
      await commitCardField('b.tools_used', next, had ? 'correct' : 'confirm');
    },
    [commitCardField, ctx.section_b],
  );

  const reloadSchema = useCallback(async () => {
    try {
      const s = await fetchSchema(undefined, clientId ?? undefined);
      setFields(s.fields);
      setSections(s.sections);
    } catch (e) {
      console.error('[orbit] schema reload after add failed', e);
    }
  }, []);

  if (pluginScoping) {
    return (
      <div
        className={`orbit-intake-root orbit-intake-root--chat${isPanel ? ' orbit-intake-root--panel' : ''}`}
        data-theme={theme}
      >
        {!isPanel && (
          <header className="aa-topbar">
            <div className="aa-brand">
              <div className="aa-brand-mark" aria-hidden="true">
                <img src="/orbit/logos/aa-logo-icon-white.svg" alt="" width={18} height={18} />
              </div>
              <span className="aa-brand-name">Orbit</span>
            </div>
          </header>
        )}
        <BuilderScopingPane />
      </div>
    );
  }

  return (
    <div
      className={`orbit-intake-root orbit-intake-root--chat${isPanel ? ' orbit-intake-root--panel' : ''}`}
      data-theme={theme}
    >
      {!isPanel && (
      <header className="aa-topbar">
        <div className="aa-brand">
          <div className="aa-brand-mark" aria-hidden="true">
            <img src="/orbit/logos/aa-logo-icon-white.svg" alt="" width={18} height={18} />
          </div>
          <span className="aa-brand-name">Orbit</span>
        </div>
        <div className="aa-topbar-right">
          {/* v0.5 — Skip-to-dashboard. Quiet persistent option. State is
              already saved server-side; the user can bring Orbit back up
              from My Business anytime. Hidden on cold-start (no captures
              yet) so it doesn't compete with the opener. */}
          {counts.captured >= 1 && (
            <div className="aa-later-wrap">
              <button
                type="button"
                className="aa-skip-to-dashboard"
                onClick={() => setLaterMenuOpen(o => !o)}
                aria-haspopup="menu"
                aria-expanded={laterMenuOpen}
                aria-label="Pick up later"
                title="Pick this up later"
              >
                <span>Pick up later</span>
                <ChevronRight size={12} strokeWidth={2.5} />
              </button>
              {laterMenuOpen && (
                <>
                  <div className="aa-later-overlay" onClick={() => setLaterMenuOpen(false)} aria-hidden="true" />
                  <div className="aa-later-menu" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      className="aa-later-item"
                      onClick={() => { setLaterMenuOpen(false); navigate('/dashboard'); }}
                    >
                      <LayoutDashboard size={15} strokeWidth={2} />
                      <span className="aa-later-item-body">
                        <span className="aa-later-item-title">Go to dashboard</span>
                        <span className="aa-later-item-sub">Your scope is saved — resume anytime.</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="aa-later-item"
                      onClick={() => { setLaterMenuOpen(false); setScheduleOpen(true); }}
                    >
                      <CalendarClock size={15} strokeWidth={2} />
                      <span className="aa-later-item-body">
                        <span className="aa-later-item-title">Schedule a call</span>
                        <span className="aa-later-item-sub">Orbit calls you to finish — picks up where you left off.</span>
                      </span>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          {/* Compact dossier pill — replaces the full brain strip. Shows
              captured/total count + opens the full dossier drawer on
              click. The aa-dossier-pill--fresh class pulses briefly
              whenever a new field gets inferred from prose so the user
              feels their answers landing without dedicating screen real
              estate to a full pill row. */}
          {scopedCounts.total > 0 && (
            <button
              type="button"
              className={
                'aa-dossier-pill'
                + (freshKeys.length > 0 ? ' aa-dossier-pill--fresh' : '')
              }
              onClick={() => setMobileDrawerOpen(true)}
              aria-label={`Dossier ${scopedCounts.captured} of ${scopedCounts.total} filled — open`}
              title="Open full dossier"
            >
              <span className="aa-dossier-pill-dot" aria-hidden="true" />
              <span className="aa-dossier-pill-label">
                {orbitSession?.job === 'map_business' ? 'Map' : orbitSession?.job === 'scope_problem' ? 'Scope' : 'Dossier'}
              </span>
              <span className="aa-dossier-pill-count">
                {orbitSession?.position?.batch_id
                  ? `Step ${(orbitSession.position.batches_done?.length ?? 0) + 1}`
                  : scopedCounts.captured}
                {!orbitSession?.position?.batch_id && (
                  <>
                    <span className="aa-dossier-pill-sep">/</span>
                    {scopedCounts.total}
                  </>
                )}
              </span>
            </button>
          )}
          <button
            type="button"
            className="aa-theme-toggle"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          >
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
          </button>
        </div>
      </header>
      )}

      <main className="aa-chat-shell aa-chat-shell--pane">
        {/* Brain strip removed — the inline pill row competed with the
            active question for attention. The dossier count moved to a
            compact topbar pill (above) and the full record opens via
            the drawer. Fresh-inference pulse animation now lives on
            the topbar pill instead. */}

        {/* Center stage — one turn fills the pane. Past turns are
            navigable via prev/next; the latest is interactive. Touch
            handlers fire prev/next on a horizontal flick. */}
        {/* MAP THE BUSINESS runs as a walk: one question at a time, phases and
            a progress bar — the same shape an invited participant gets. It
            replaces the free-form pane AND its composer on this route, so the
            problem and explore routes (and the header/drawer mounts on them)
            are untouched. */}
        {walkActive && companyMode === 'cards' ? (
          <section className="aa-cards-stage">
            {(
              <>
                <SectionCardFlow
                  fields={fields}
                  context={ctx}
                  startAtLetter={cardStartLetter}
                  onCommit={commitCardField}
                  renderSectionFooter={(letter) => (
                    <div className="flex flex-col gap-4">
                      {/* Josh's order: the stack questions are the field rows
                          above; the tools appear only after them. */}
                      {letter === 'b' && (
                        <StackToolsPanel
                          tools={toStringArray(ctx.section_b?.tools_used)}
                          connections={connections}
                          clientId={clientId ?? undefined}
                          onToolsChange={saveTools}
                          onConnected={() => { void reloadConnections(); }}
                          optionLabels={toolOptionLabels}
                        />
                      )}
                      {/* A7 — what the rows above say the company told us, next
                          to what is actually on the team, with headcounts. No
                          list of individual people: that is the Team page. */}
                      {letter === 'c' && (
                        <DepartmentsPanel
                          clientId={clientId ?? undefined}
                          orgChartUrl={asText(ctx.section_c?.org_chart_url)}
                        />
                      )}
                      {/* A5 — the five D fields stay above; these are the
                          repeating goal records beside them. */}
                      {letter === 'd' && (
                        <GoalsPanel clientId={clientId ?? undefined} />
                      )}
                      <AddFieldControl
                        section={letter}
                        clientId={clientId ?? undefined}
                        onAdded={() => { void reloadSchema(); }}
                      />
                    </div>
                  )}
                  renderCatchAll={() => (
                    <CatchAllCard
                      fields={fields}
                      clientId={clientId ?? undefined}
                      onAdded={() => { void reloadSchema(); }}
                    />
                  )}
                  onFinish={() => {
                    onAfterTurnRef.current?.();
                    void finishMapping();
                  }}
                />
                <p className="aa-cards-switch">
                  Would you rather be asked one question at a time?{' '}
                  <button type="button" onClick={() => setCompanyMode('walk')}>
                    Walk me through it
                  </button>
                </p>
              </>
            )}
          </section>
        ) : walkActive ? (
          <BusinessWalk
            clientId={clientId}
            onAfterAnswer={() => { refreshState().catch(() => {}); }}
            onDone={() => {
              refreshState().catch(() => {});
              onAfterTurnRef.current?.();
              // The walk only renders on the company route, so its finish line
              // is the business — never the dashboard.
              exitOrbit({ home: '/my-business' });
            }}
            onSeeBusiness={() => goAndClose('/my-business')}
            onScopeProblem={() => navigate('/projects')}
          />
        ) : (
        <>
        <section
          className="aa-pane-stage"
          onTouchStart={handleStageTouchStart}
          onTouchEnd={handleStageTouchEnd}
        >
          {(() => {
            if (turns.length === 0 || activeTurnIdx < 0) {
              // Pre-opener empty state — Orbit hasn't spoken yet. The
              // composer + brain strip cover the moment; no pane to
              // render until the first turn lands.
              return (
                <div className="aa-pane-empty">
                  <span className="aa-pane-empty-eyebrow">Welcome.</span>
                  <p className="aa-pane-empty-line">
                    Orbit will start in a second. Or kick off — tell me about
                    your business, drop a doc, paste a url.
                  </p>
                </div>
              );
            }
            // Guard a STALE activeTurnIdx that points PAST the end. Picking
            // "Map the business" (or any route) fires setTurns() to strip the
            // route_chooser turn; on the render right after, turns is shorter
            // but activeTurnIdx still equals the old last index, and the
            // index-clamping effect hasn't run yet. Without this clamp,
            // turns[activeTurnIdx] is undefined and `t.role` below throws
            // ("Cannot read properties of undefined (reading 'role')"). The
            // outer guard already handles activeTurnIdx < 0 / empty turns, so
            // safeIdx is always in range here.
            const safeIdx = Math.min(activeTurnIdx, turns.length - 1);
            const t = turns[safeIdx];
            const isLatest = safeIdx === turns.length - 1;
            // Map turn role → pane variant.
            const variantFor: Record<Turn['role'], TurnPaneVariant> = {
              orbit: 'orbit',
              user: 'user',
              system: 'system',
              batch_confirm: 'card',
              compound_chips: 'card',
              anything_else: 'card',
              problem_canvas: 'card',
              prd_progress: 'card',
              prd_review: 'card',
              field_suggestions: 'card',
              url_reading: 'card',
              website_intake: 'card',
              route_chooser: 'card',
            };
            // While waiting for Orbit on a just-sent message, present the
            // user turn through the ORBIT (left) shell — not the right-hand
            // blue bubble — so the pending state mirrors the final layout
            // ("You said" recap + Orbit's reply) and nothing jumps when the
            // reply lands.
            const awaitingReply = t.role === 'user' && isLatest && pending;
            const variant = awaitingReply ? 'orbit' : variantFor[t.role];
            // Section tag only meaningful for orbit chat turns asking
            // an L1/L2 field — directive cards carry their own header.
            const sectionTag =
              t.role === 'orbit'
                ? (activeQuestion?.field && isLatest
                    ? sectionTagFor(activeQuestion.field)
                    : t.sectionTag ?? null)
                : null;

            return (
              <TurnPane
                index={navPos >= 0 ? navPos : 0}
                total={navTotal}
                sectionTag={sectionTag}
                variant={variant}
                isLatest={isLatest}
                transitionKey={activeTurnIdx}
                wide={t.role === 'route_chooser'}
              >
                {(() => {
                  if (t.role === 'system') {
                    return <ChatTurn role="system" text={t.text} systemKind={t.kind} />;
                  }
                  if (t.role === 'user') {
                    // Pending: show the message as the quiet "You said"
                    // recap (same as the final state) with Orbit's thinking
                    // line below — seamless when the reply arrives. Past
                    // user turns in history keep the plain bubble.
                    if (awaitingReply) {
                      return (
                        <>
                          <div className="aa-pane-prev-user">
                            <span className="aa-pane-prev-user-label">You said</span>
                            <p className="aa-pane-prev-user-text">{t.text}</p>
                          </div>
                          <OrbitThinking status={thinkingStatus} understood={streamAck} reply={streamReply} />
                        </>
                      );
                    }
                    return <ChatTurn role="user" text={t.text} attachment={t.attachment} />;
                  }
                  if (t.role === 'batch_confirm') {
                    return (
                      <BatchConfirmCard
                        payload={t.payload}
                        clientId={clientId ?? undefined}
                        onSubmitted={(res) => handleBatchConfirmSubmitted(activeTurnIdx, res, t.payload)}
                      />
                    );
                  }
                  if (t.role === 'compound_chips') {
                    return (
                      <CompoundChipCard
                        payload={t.payload}
                        clientId={clientId ?? undefined}
                        onSubmitted={(res) => handleBatchConfirmSubmitted(activeTurnIdx, res)}
                      />
                    );
                  }
                  if (t.role === 'anything_else') {
                    return (
                      <AnythingElseCard
                        payload={t.payload}
                        clientId={clientId ?? undefined}
                        onSubmitted={(res, bc) => handleAnythingElseSubmitted(activeTurnIdx, res, bc)}
                        onSkipped={() => handleAnythingElseSkipped(activeTurnIdx)}
                      />
                    );
                  }
                  if (t.role === 'problem_canvas') {
                    return (
                      <ProblemCanvas
                        payload={t.payload}
                        onContinue={() => handleProblemCanvasContinue(activeTurnIdx)}
                        onDraftPrdNow={handleDraftPrdAccept}
                      />
                    );
                  }
                  if (t.role === 'prd_progress') {
                    return (
                      <PrdDraftProgressCard
                        payload={t.payload}
                        clientId={clientId ?? undefined}
                        onDone={() => { void handlePrdDraftDone(); }}
                        // Only the live draft polls: once done, the review
                        // turn is appended after this one and polling stops.
                        // Historical cards in restored threads never poll.
                        active={isLatest}
                      />
                    );
                  }
                  if (t.role === 'prd_review') {
                    return (
                      <PrdInlineReviewCard
                        payload={t.payload}
                        clientId={clientId ?? undefined}
                        onSeeOnWorkspace={(pid) => {
                          if (pid) void openWorkspace(pid);
                          else navigate('/dashboard');
                        }}
                        onGoToDashboard={() => navigate('/dashboard')}
                        // On the dashboard side panel you're already on the
                        // dashboard — only the intake page needs this button.
                        hideDashboard={isPanel}
                      />
                    );
                  }
                  if (t.role === 'field_suggestions') {
                    return (
                      <FieldSuggestionsCard
                        payload={t.payload}
                        onPick={(value) => handleFieldSuggestionPick(activeTurnIdx, value)}
                        onWriteOwn={handleFieldSuggestionWriteOwn}
                        disabled={t.picked || pending}
                      />
                    );
                  }
                  if (t.role === 'route_chooser') {
                    return <RouteChooser onPick={handleRoutePick} disabled={pending} />;
                  }
                  if (t.role === 'website_intake') {
                    return (
                      <WebsiteStep
                        isScanning={pending}
                        error={t.error ?? null}
                        initialUrl={t.lastUrl ?? websiteGuess}
                        onSubmitUrl={v => handleLandingRead({ kind: 'url', value: v })}
                        onSubmitText={v => handleLandingRead({ kind: 'text', value: v })}
                        onSubmitDoc={f => handleLandingRead({ kind: 'doc', file: f })}
                        onSkip={handleLandingSkip}
                      />
                    );
                  }
                  if (t.role === 'url_reading') {
                    return <IngestProgressCard host={t.host} kind={t.kind ?? 'url'} />;
                  }
                  // ── ORBIT QUESTION TURN ────────────────────────
                  // Only the LATEST turn can be answered. Past turns
                  // render as read-only history (chips disabled).
                  const turnField = t.field ?? null;
                  const isCurrentQuestion = Boolean(
                    activeQuestion?.field
                    && turnField
                    && turnField === activeQuestion.field,
                  );
                  const isLegacyCurrentQuestion = Boolean(
                    !turnField
                    && t.active
                    && isLatest,
                  );
                  const canAnswerTurn = isLatest && (isCurrentQuestion || isLegacyCurrentQuestion);
                  const isMultiselect =
                    canAnswerTurn
                    && activeQuestion?.kind === 'multiselect';
                  const isDateQuestion =
                    canAnswerTurn
                    && activeQuestion?.kind === 'date';
                  const selectedForActive = (() => {
                    if (!isMultiselect || !activeQuestion?.field) return undefined;
                    const [letter, subkey] = activeQuestion.field.split('.');
                    const section = (ctx as any)[`section_${letter}`] as Record<string, unknown> | undefined;
                    const existing = section?.[subkey];
                    if (Array.isArray(existing)) {
                      return existing.filter((v): v is string => typeof v === 'string');
                    }
                    return [];
                  })();
                  const renderedQuestion = canAnswerTurn ? activeQuestion : null;
                  const fk = canAnswerTurn ? activeQuestion?.field : t.field;
                  const def = fk ? fields.find(f => f.key === fk) : undefined;
                  return (
                    <>
                    {precedingUserContext && (
                      <div className="aa-pane-prev-user">
                        <span className="aa-pane-prev-user-label">
                          You said
                        </span>
                        <p className="aa-pane-prev-user-text">
                          {precedingUserContext.text}
                        </p>
                      </div>
                    )}
                    {t.phase && PHASE_BANNER[t.phase] && (
                      <div className="aa-pane-phase-banner">
                        {PHASE_BANNER[t.phase]}
                      </div>
                    )}
                    <ChatTurn
                      role="orbit"
                      text={t.reply || renderedQuestion?.prompt || t.text}
                      why={renderedQuestion?.why ?? t.why}
                      chips={renderedQuestion ? chipsWithSkipMaybe(renderedQuestion) : t.chips}
                      active={t.active}
                      sectionTag={null /* the pane shell renders the eyebrow; ChatTurn skips it */}
                      variant={t.variant ?? 'normal'}
                      compact={compactCards}
                      multiselect={isMultiselect}
                      selectedValues={selectedForActive}
                      onMultiSubmit={canAnswerTurn ? handleMultiSubmit : undefined}
                      datePicker={isDateQuestion}
                      dateValue={isDateQuestion ? currentDateFor(activeQuestion?.field) : null}
                      onDateSubmit={isDateQuestion ? handleDateSubmit : undefined}
                      action={
                        t.actionData
                          ? {
                              label: t.actionData.label,
                              detail: t.actionData.detail,
                              onClick: () => {
                                const a = t.actionData!;
                                if (a.external) {
                                  window.open(a.route, '_blank', 'noopener,noreferrer');
                                } else {
                                  navigate(a.route);
                                }
                              },
                            }
                          : null
                      }
                      onChipSelect={canAnswerTurn ? handleChipSelect : undefined}
                      directiveType={t.directiveType ?? null}
                      directivePayload={t.directivePayload ?? null}
                      onDirectiveScan={sendUrl}
                      onApproachPick={(id: string) => handleApproachPick(id as Approach)}
                      picking={picking}
                      onDraftPrd={handleDraftPrdAccept}
                      onEditBeforePrd={handleDraftPrdEdit}
                      onAddMoreBeforePrd={handleDraftPrdAddMore}
                      drafting={prdDrafting}
                      onOpenCalendar={handleOpenCalendar}
                      onBookPaidHumanSlot={handleBookPaidHumanSlot}
                      onDismissPaidHuman={handleDismissPaidHuman}
                      onBackPaidHuman={handleBackFromPaidHuman}
                      onPickMarketplace={handlePickMarketplace}
                      onPickCustomBuild={handlePickCustomBuild}
                      marketplacePicking={marketplacePicking}
                      onEnterL2Continue={handleEnterL2Continue}
                      onEnterL2TalkToPartner={handleEnterL2TalkToPartner}
                      respondentRole={def?.role ?? null}
                      roleFieldKey={fk ?? null}
                      onShareField={handleShareField}
                      onScheduleField={handleScheduleField}
                    />
                    {/* On-demand coaching (problem flow). For free-type fields
                        with no chips (success criteria, workflows, trigger…),
                        show a compact button UNDER the question. Tap → loading
                        → Orbit generates option blocks inline. Not auto. */}
                    {canAnswerTurn && fk && COACHABLE_FIELDS.has(fk) && !t.fieldSuggestions && (
                      coachLoadingField === fk ? (
                        <div className="aa-coach-loading" role="status">
                          <span className="aa-coach-loading-dot" />
                          <span className="aa-coach-loading-dot" />
                          <span className="aa-coach-loading-dot" />
                          <span className="aa-coach-loading-text">Crafting a few ways to answer…</span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="aa-coach-cta"
                          onClick={() => void requestFieldCoach(fk)}
                          disabled={pending}
                        >
                          <Sparkles size={13} strokeWidth={2.25} />
                          Show me a few ways to answer
                        </button>
                      )
                    )}
                    {/* Option blocks render UNDER the question once generated,
                        so the question stays visible. */}
                    {canAnswerTurn && t.fieldSuggestions
                      && Array.isArray(t.fieldSuggestions.candidates)
                      && t.fieldSuggestions.candidates.length >= 2 && (
                      <FieldSuggestionsCard
                        payload={t.fieldSuggestions}
                        onPick={(value) => { void sendText(value); }}
                        onWriteOwn={handleFieldSuggestionWriteOwn}
                        disabled={pending}
                      />
                    )}
                    {pairedUserAnswer && (
                      <div className="aa-pane-user-answer">
                        <span className="aa-pane-user-answer-label">
                          Your answer
                        </span>
                        <p className="aa-pane-user-answer-text">
                          {pairedUserAnswer.text}
                        </p>
                      </div>
                    )}
                    </>
                  );
                })()}
              </TurnPane>
            );
          })()}

          {/* Thinking indicator — only on the latest turn while a request
              is in flight. For a just-sent USER message it's rendered
              INLINE under the "You said" recap (above), so skip it here to
              avoid a double. For chip/confirm turns (no user turn pushed)
              it shows under the active question. Same brand-true serif
              "Orbit" + breathing dots as the inline one. */}
          {pending
            && activeTurnIdx === turns.length - 1
            && turns[turns.length - 1]?.role !== 'user'
            && (
              <OrbitThinking status={thinkingStatus} understood={streamAck} reply={streamReply} />
          )}

          {/* Soft recommendation pills moved out of the stage — they
              now ride attached to the composer's top edge (see below)
              so they read as a CTA-row glued to the input, not as
              floating chat content. */}

          {/* Education panel — only on the latest turn pre-acknowledge. */}
          {shouldShowEducation && activeTurnIdx === turns.length - 1 && (
            <div className="aa-pane-education">
              <EducationPanel onAcknowledge={() => setEduDismissed(true)} />
            </div>
          )}
        </section>

        {/* Pane navigation — prev / counter / next / jump-to-latest.
            Inner-wrap centers the nav content to the chrome max-width
            (1080px) on large screens so it aligns with the brain strip
            and composer. */}
        {turns.length > 1 && (
          <nav className="aa-pane-nav" aria-label="Move between turns">
            <div className="aa-pane-nav-inner">
              <button
                type="button"
                className="aa-pane-nav-btn aa-pane-nav-btn--prev"
                onClick={goPrev}
                disabled={activeTurnIdx <= 0}
                aria-label="Previous turn"
              >
                <ChevronRight size={14} strokeWidth={2.5} style={{ transform: 'rotate(180deg)' }} />
                <span>Prev</span>
              </button>
              <span className="aa-pane-nav-count">
                {String(Math.max(navPos + 1, 1)).padStart(2, '0')}
                <span className="aa-pane-nav-count-sep"> / </span>
                {String(Math.max(navTotal, 1)).padStart(2, '0')}
              </span>
              {activeTurnIdx < turns.length - 1 ? (
                <>
                  <button
                    type="button"
                    className="aa-pane-nav-btn aa-pane-nav-btn--next"
                    onClick={goNext}
                    disabled={activeTurnIdx >= turns.length - 1}
                    aria-label="Next turn"
                  >
                    <span>Next</span>
                    <ChevronRight size={14} strokeWidth={2.5} />
                  </button>
                  <button
                    type="button"
                    className="aa-pane-nav-latest"
                    onClick={goLatest}
                    aria-label="Jump back to the current question"
                  >
                    Jump to current
                  </button>
                </>
              ) : (
                <span className="aa-pane-nav-current">Current</span>
              )}
            </div>
          </nav>
        )}

        {/* Composer pinned at the bottom. Inner-wrap caps to the chrome
            max-width (1080px). Disabled when viewing a past turn — the
            user needs to swipe forward to answer.

            Soft recommendation pills ride ATTACHED to the composer's
            top edge (same inner-wrap width) so they read as a CTA-row
            welded to the input, not as floating chat content. Only on
            the latest turn — past panes hide them.

            Suppressed entirely on the landing chooser and the landing read —
            neither captures through the composer. */}
        {!chooserActive && !landingReadActive && (
        <div className="aa-pane-composer">
          <div className="aa-pane-composer-inner">
            {activeTurnIdx === turns.length - 1 && !isCompanyRoute && (
              <div className="aa-pane-composer-affordances">
                <PaidHumanRecommendedPill
                  suggestion={suggestion}
                  alreadyBooked={paidHumanAlreadyBooked}
                  alreadyOpened={paidHumanPillOpened}
                  onOpen={handleOpenPaidHumanFromPill}
                />
                <RecommendationPill
                  plan={routePlan}
                  suggestion={suggestion}
                  onPick={handleApproachPick}
                  picking={picking}
                  onResume={() => {
                    const route = routePlan?.next_best_action?.route;
                    if (route) navigate(route);
                  }}
                />
              </div>
            )}
            {/* VOICE FIRST — a live spoken conversation with Orbit is the
                default way in; the report is written at the END from the
                transcript, through the SAME pipeline typing uses. Typing is the
                second way ("type instead"), a booked call the third. */}
            {showText ? (
              <>
              {/* The door swings both ways: voice is the default, and someone
                  who switched to typing can go back to it mid-conversation. */}
              {MIC_AVAILABLE && (
                <button
                  type="button"
                  className="aa-livevoice-alt aa-livevoice-alt--above"
                  onClick={() => setShowText(false)}
                >
                  <Mic size={13} strokeWidth={2.25} /> Talk to Orbit instead
                </button>
              )}
              <Composer
                onSendText={sendText}
                onSendUrl={sendUrl}
                onUploadDoc={sendDoc}
                placeholder={
                  coldStart
                    ? 'Tell me about your business.'
                    : 'Answer, paste, drop, or speak.'
                }
                // Composer is ALWAYS live (only blocked while a request is
                // in flight). The user must never be locked out of typing
                // — even when reviewing a past turn. sendText/sendUrl push
                // to the end and the pane auto-advances, so sending while
                // scrolled-back simply jumps the user to current.
                disabled={pending}
              />
              </>
            ) : (
              // Orbit opens the conversation; switching to text unmounts this,
              // which ends the session cleanly.
              <OrbitLiveVoice
                clientId={clientId}
                problemId={activeProblemId}
                // A finished conversation is not a destination. The typed flow
                // ends on a PRD card with "see it on the workspace" / "go to the
                // dashboard"; spoken, there is no card to click — so the session's
                // own outcome decides where the person lands: the problem it just
                // scoped, else the home of the route they were on.
                onEnded={(outcome) => {
                  refreshState().catch(() => {});
                  exitOrbit({ problemId: outcome?.problem_id });
                }}
                onTypeInstead={() => setShowText(true)}
                onScheduleCall={() => setScheduleOpen(true)}
                autoStart
                disabled={pending}
              />
            )}
          </div>
        </div>
        )}
        </>
        )}
      </main>

      {/* Mobile drawer (sub-1000px) */}
      <DossierDrawer
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        fields={fields}
        sections={sections}
        ctx={ctx}
        activeField={activeQuestion?.field ?? null}
        capturedCount={counts.captured}
        totalCount={counts.total}
        onCommit={handleCommitFromDrawer}
      />

      {/* Pick-up-later → schedule an Orbit call that resumes this intake. */}
      <ScheduleCallModal
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        clientId={clientId ?? undefined}
      />
    </div>
  );
}

export default OrbitIntake;
